import { BookCover } from "../components/BookCover";
import { InputBudgetRecoveryPanel } from "../components/InputBudgetRecoveryPanel";
import { ModelCallReceipt } from "../components/ModelCallReceipt.js";
import { ChapterTitleReceipt } from "../components/ChapterTitleReceipt";
import { PlannedChapterTitleReceipt } from "../components/PlannedChapterTitleReceipt";
import { reviewReceiptLabel } from "../review-presentation";
import { AgentFlowLauncher } from "../components/AgentFlowLauncher";
import { CreativeWorkflowReceipt } from "../components/CreativeWorkflowReceipt";
import {
  Activity,
  AlertTriangle,
  BookOpenText,
  BrainCircuit,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleDashed,
  Compass,
  FileText,
  Filter,
  Gauge,
  Grid,
  History,
  Layers,
  ListChecks,
  Maximize2,
  MessageSquarePlus,
  Minimize2,
  MoreHorizontal,
  Pause,
  Play,
  RotateCcw,
  Save,
  Search,
  Send,
  SlidersHorizontal,
  Sparkles,
  Square,
  Terminal,
  Trash2,
  WandSparkles,
  X
} from "lucide-react";
import { type FormEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, postJson, putJson } from "../api";
import { Modal } from "../components/Modal";
import { choiceKeyDown, useMenuKeyboard } from "../keyboard";
import { confirmAction, confirmDiscard, useSaveShortcut, useUnsavedChanges } from "../interaction";
import { EmptyState, ScoreBadge, StatusDot, formatNumber, relativeTime } from "../components/Ui";
import type { AgentConfig, BootstrapPayload, ChapterDetail, ChapterRevision, ChapterUserRevision, FixScope, NovelRun, PipelineStage, PlanExtensionJob, RunEvent, RunStageDetail, ScoreDecisionReason, ScoreResult, ScoreRound } from "../types";

type EditorTab = "text" | "card" | "review";

interface MilestoneGroup {
  id: string;
  title: string;
  subtitle: string;
  stageIds: string[];
}

export interface PipelineStepItem {
  id: string;
  label: string;
  agentId: string;
  agentShortName: string;
  status: "passed" | "running" | "failed" | "waiting";
  detail: string;
}

const STAGE_AGENT_MAP: Record<string, { agentId: string; shortName: string; defaultLabel: string }> = {
  context: { agentId: "chapter-planner", shortName: "临写", defaultLabel: "临写规划与资料编排" },
  preflight: { agentId: "planner_reviewer", shortName: "预检", defaultLabel: "架构冲突预检" },
  write: { agentId: "writer", shortName: "Writer", defaultLabel: "正文写作 Agent" },
  candidates: { agentId: "writer", shortName: "多候选", defaultLabel: "多候选对比推演" },
  "narrative-review": { agentId: "plot-scorer", shortName: "叙事", defaultLabel: "叙事结构审查" },
  score: { agentId: "plot-scorer", shortName: "评分组", defaultLabel: "分维度评审" },
  polish: { agentId: "optimizer", shortName: "精修", defaultLabel: "文风精修优化" },
  revise: { agentId: "writer", shortName: "返修", defaultLabel: "场景剧情返修" },
  gate: { agentId: "gate", shortName: "门禁", defaultLabel: "终审门禁 Agent" },
  ledger: { agentId: "ledger", shortName: "结算", defaultLabel: "事实观察、结算与独立校验" },
  "pattern-audit": { agentId: "gate", shortName: "套路", defaultLabel: "叙事指纹检测" },
  checkpoint: { agentId: "checkpoint", shortName: "批检", defaultLabel: "周期批检 Agent" }
};

const PIPELINE_MILESTONES: MilestoneGroup[] = [
  {
    id: "prep",
    title: "资料编排与预检",
    subtitle: "最新正文驱动的临写备忘与资料编排",
    stageIds: ["context", "preflight"]
  },
  {
    id: "draft",
    title: "独立正文创作",
    subtitle: "章节写作与关键章匿名候选",
    stageIds: ["write", "candidates"]
  },
  {
    id: "review",
    title: "原稿审查与按需返修",
    subtitle: "原稿直评；仅按正文证据分流修改",
    stageIds: ["narrative-review", "score", "polish", "revise"]
  },
  {
    id: "governance",
    title: "终稿门禁与账本",
    subtitle: "终稿观察、候选账本校验与版本提交",
    stageIds: ["gate", "ledger", "pattern-audit", "checkpoint"]
  }
];

interface WorkspaceProps {
  initialChapterNumber?: number;
  data: BootstrapPayload;
  run: NovelRun | null;
  events: RunEvent[];
  onStartRun: (start: number, end: number) => Promise<void>;
  onRewriteChapter: (chapter: number) => Promise<void>;
  onControlRun: (action: "pause" | "resume" | "stop") => Promise<void>;
  onChanged: () => Promise<void>;
  onOpenReader: (chapter: number) => void;
  onOpenBible?: () => void;
  notify: (message: string, tone?: "success" | "error" | "info") => void;
}

export function WorkspaceView({ initialChapterNumber, data, run, events, onStartRun, onRewriteChapter, onControlRun, onChanged, onOpenReader, onOpenBible, notify }: WorkspaceProps) {
  const initialChapter = data.chapters.some((chapter) => chapter.number === initialChapterNumber) ? initialChapterNumber! : Math.max(1, data.project.currentChapter || 1);
  const [selected, setSelectedRaw] = useState(initialChapter);
  const [detail, setDetail] = useState<ChapterDetail | null>(null);
  const [content, setContent] = useState("");
  const [synopsisContent, setSynopsisContent] = useState("");
  const [coreEvent, setCoreEvent] = useState("");
  const [supportingProgressText, setSupportingProgressText] = useState("");
  const [compressionNotesText, setCompressionNotesText] = useState("");
  const [dramaticQuestion, setDramaticQuestion] = useState("");
  const [hardOutcomesText, setHardOutcomesText] = useState("");
  const [endingState, setEndingState] = useState("");
  const [mustAvoidText, setMustAvoidText] = useState("");
  const [creativeFreedom, setCreativeFreedom] = useState("");
  const [tab, setTab] = useState<EditorTab>("text");
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runDialog, setRunDialog] = useState(false);
  const [startChapter, setStartChapter] = useState(data.project.currentChapter + 1);
  const [endChapter, setEndChapter] = useState(data.project.currentChapter + 1);
  const [actionMenu, setActionMenu] = useState(false);
  const [chapterAction, setChapterAction] = useState<"delete" | "rewrite" | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rebuildHistoryOpen, setRebuildHistoryOpen] = useState(false);
  const [revisions, setRevisions] = useState<ChapterRevision[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [scoreRounds, setScoreRounds] = useState<ScoreRound[]>([]);
  const [scoreRoundsBusy, setScoreRoundsBusy] = useState(false);
  const [scoreRoundsError, setScoreRoundsError] = useState("");
  const [scoreReload, setScoreReload] = useState(0);
  const [feedbackLoadBusy, setFeedbackLoadBusy] = useState(false);
  const [feedbackLoadError, setFeedbackLoadError] = useState("");
  const [expandedRound, setExpandedRound] = useState<number | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [userRevisions, setUserRevisions] = useState<ChapterUserRevision[]>([]);
  const [stageDetailTarget, setStageDetailTarget] = useState<{ stage: PipelineStage; chapter: number } | null>(null);
  const [selectedAgentInfo, setSelectedAgentInfo] = useState<{ agentId: string; agentName: string; stageId: string; stageLabel: string; status: string; chapter: number } | null>(null);
  const [stageDetail, setStageDetail] = useState<RunStageDetail | null>(null);
  const [stageDetailBusy, setStageDetailBusy] = useState(false);
  const [planExtensionBusy, setPlanExtensionBusy] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [chapterError, setChapterError] = useState("");
  const [chapterReload, setChapterReload] = useState(0);
  const [runStarting, setRunStarting] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [stageError, setStageError] = useState("");
  const [matrixPage, setMatrixPage] = useState(1);
  const saveLock = useRef(false);
  const runLock = useRef(false);
  const controlLock = useRef(false);
  const chapterNavigationLock = useRef(false);
  const stageSequence = useRef(0);
  const mutationLock = useRef(false);
  const historySequence = useRef(0);
  const selectedRef = useRef(selected); selectedRef.current = selected;
  const textDirty = Boolean(detail && content !== (detail.status === "final" ? detail.finalText : detail.draft || detail.finalText));
  const cardDirty = Boolean(detail && JSON.stringify([synopsisContent, coreEvent, supportingProgressText, compressionNotesText, dramaticQuestion, hardOutcomesText, endingState, mustAvoidText, creativeFreedom]) !== JSON.stringify([detail.synopsis, detail.coreEvent, detail.supportingProgress.join("\n"), detail.compressionNotes.join("\n"), detail.dramaticQuestion, detail.hardOutcomes.join("\n"), detail.endingState, detail.mustAvoid.join("\n"), detail.creativeFreedom ?? ""]));
  const dirty = textDirty || cardDirty;
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  useUnsavedChanges(dirty || Boolean(feedbackText.trim()), `第 ${selected} 章正文、章卡或修改意见`, saving || actionBusy || feedbackBusy || runStarting || controlBusy || planExtensionBusy);
  async function setSelected(number: number): Promise<boolean> {
    if (number === selected) return true;
    if (chapterNavigationLock.current) return false;
    if (!data.chapters.some((chapter) => chapter.number === number)) { notify("该章节尚未建立，请先生成后续章纲", "info"); return false; }
    if (saving || actionBusy || feedbackBusy || runStarting || controlBusy || planExtensionBusy) { notify("当前操作尚未完成，请勿切换章节", "info"); return false; }
    chapterNavigationLock.current = true;
    try {
      if (!await confirmDiscard(dirty || Boolean(feedbackText.trim()), `第 ${selected} 章的编辑`)) { setJumpInput(String(selected)); return false; }
      setSelectedRaw(number); setFeedbackText(""); setStageDetail(null); setStageDetailTarget(null); setSelectedAgentInfo(null); stageSequence.current++;
      return true;
    } finally { chapterNavigationLock.current = false; }
  }

  // 指挥台与实时记录状态
  const [logFilter, setLogFilter] = useState<"all" | "decision" | "score" | "error">("all");
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [expandedMilestones, setExpandedMilestones] = useState<Set<string>>(new Set());
  const manualMilestoneContext = useRef("");
  const logScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!actionMenu) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !actionMenuRef.current?.contains(event.target) && !document.querySelector(".chapter-action-popover")?.contains(event.target)) {
        setActionMenu(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [actionMenu]);

  const latestChapterOutput = useMemo(
    () => [...events].reverse().find((event) => event.chapter === selected && event.type === "output")?.id ?? "",
    [events, selected]
  );

  useEffect(() => {
    if (run?.id && run.currentChapter && !dirtyRef.current && initialChapterNumber === undefined) setSelectedRaw(run.currentChapter);
    setStageDetailTarget(null);
    setStageDetail(null);
  }, [run?.id]);

  useEffect(() => {
    let cancelled = false;
    if (dirtyRef.current && detail?.number === selected) return;
    setLoadingChapter(true); setChapterError(""); setDetail(null); setContent("");
    setUserRevisions([]);
    api<ChapterDetail>(`/api/projects/${data.project.id}/chapters/${selected}`)
      .then((chapter) => {
        if (cancelled) return;
        setDetail(chapter);
        setContent(chapter.status === "final" ? chapter.finalText : chapter.draft || chapter.finalText);
        setSynopsisContent(chapter.synopsis);
        setCoreEvent(chapter.coreEvent);
        setSupportingProgressText(chapter.supportingProgress.join("\n"));
        setCompressionNotesText(chapter.compressionNotes.join("\n"));
        setDramaticQuestion(chapter.dramaticQuestion);
        setHardOutcomesText(chapter.hardOutcomes.join("\n"));
        setEndingState(chapter.endingState);
        setMustAvoidText(chapter.mustAvoid.join("\n"));
        setCreativeFreedom(chapter.creativeFreedom ?? "");
      })
      .catch((error) => { if (!cancelled) setChapterError(error instanceof Error ? error.message : "章节加载失败"); })
      .finally(() => !cancelled && setLoadingChapter(false));
    return () => { cancelled = true; };
  }, [data.project.id, selected, latestChapterOutput, chapterReload]);

  const recentRunForChapter = data.recentRuns.find((item) => selected >= item.startChapter && selected <= item.endChapter) ?? null;
  const reviewRun = run && selected >= run.startChapter && selected <= run.endChapter ? run : recentRunForChapter;
  const inspectorRun = run ?? recentRunForChapter ?? data.recentRuns[0] ?? null;
  const latestScoreEvent = useMemo(
    () => [...events].reverse().find((event) => event.chapter === selected && event.type === "score")?.id ?? "",
    [events, selected]
  );

  useEffect(() => {
    setScoreRounds([]); setExpandedRound(null); setScoreRoundsError(""); setScoreRoundsBusy(false);
    if (tab !== "review" || !reviewRun) return;
    const controller = new AbortController();
    setScoreRoundsBusy(true);
    api<{ rounds: ScoreRound[] }>(`/api/runs/${reviewRun.id}/score-rounds`, { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setScoreRounds(result.rounds.filter((round) => round.chapter === selected)); })
      .catch((error) => { if (!controller.signal.aborted) setScoreRoundsError(error instanceof Error ? error.message : "评分轮次加载失败"); })
      .finally(() => { if (!controller.signal.aborted) setScoreRoundsBusy(false); });
    return () => controller.abort();
  }, [tab, reviewRun?.id, selected, latestScoreEvent, scoreReload]);

  const activeUserRevision = userRevisions.find((item) => !["completed", "failed"].includes(item.status));
  useEffect(() => {
    if (!activeUserRevision) return;
    let inFlight = false; let cancelled = false;
    const timer = window.setInterval(() => {
      if (inFlight) return; inFlight = true;
      void loadUserRevisions().then(async (items) => {
        if (!cancelled) { setFeedbackLoadError(""); if (items.some((item) => item.runId)) await onChanged(); }
      }).catch(() => { if (!cancelled) setFeedbackLoadError("修改任务状态暂时无法刷新；已保留上次状态，请勿重复提交。"); }).finally(() => { inFlight = false; });
    }, 3000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [activeUserRevision?.id, activeUserRevision?.status]);

  useEffect(() => {
    if (!data.planRebuild || !["queued", "running"].includes(data.planRebuild.status)) return;
    const timer = window.setInterval(() => { void onChanged(); }, 3000);
    return () => window.clearInterval(timer);
  }, [data.planRebuild?.id, data.planRebuild?.status]);

  useEffect(() => {
    if (!data.planExtension || !["queued", "running"].includes(data.planExtension.status)) return;
    const timer = window.setInterval(() => { void onChanged(); }, 3000);
    return () => window.clearInterval(timer);
  }, [data.planExtension?.id, data.planExtension?.status]);

  useEffect(() => {
    if (run?.status === "completed") {
      void onChanged();
    }
  }, [run?.status]);

  // 创作流水线多尺度导航状态与窗口计算
  const BATCH_SIZE = 50; // 每批次 50 章，支持超长畅滑
  const totalChapters = data.project.targetChapters;
  const totalBatches = Math.max(1, Math.ceil(totalChapters / BATCH_SIZE));
  const [batchIndex, setBatchIndex] = useState(() => Math.floor((selected - 1) / BATCH_SIZE));
  const [volumeDropdownOpen, setVolumeDropdownOpen] = useState(false);
  const [jumpInput, setJumpInput] = useState(String(selected));
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixSearch, setMatrixSearch] = useState("");
  const [matrixFilter, setMatrixFilter] = useState<"all" | "final" | "ready" | "draft">("all");
  const [scrubberHover, setScrubberHover] = useState<{ percent: number; chapter: number } | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const railListRef = useRef<HTMLDivElement | null>(null);
  const volumeDropdownRef = useRef<HTMLDivElement | null>(null);
  useMenuKeyboard(actionMenu, ".chapter-action-popover", () => setActionMenu(false));
  useMenuKeyboard(volumeDropdownOpen, ".rail-volume-dropdown", () => setVolumeDropdownOpen(false));

  // 擦拭条悬停章节信息预览
  const hoveredChapterData = useMemo(() => {
    if (!scrubberHover) return null;
    return data.chapters.find((c) => c.number === scrubberHover.chapter) || null;
  }, [scrubberHover, data.chapters]);

  // 当 selected 变更时同步跳章输入框，并在需要时自动跨批次
  useEffect(() => {
    setJumpInput(String(selected));
    const targetBatch = Math.floor((selected - 1) / BATCH_SIZE);
    if (targetBatch !== batchIndex) {
      setBatchIndex(targetBatch);
    }
  }, [selected]);

  // 当 selected 或批次改变时，平滑居中选中的工位
  useEffect(() => {
    if (!railListRef.current) return;
    const timer = window.setTimeout(() => {
      if (!railListRef.current) return;
      const selectedBtn = railListRef.current.querySelector<HTMLElement>(".pipeline-station.selected");
      if (selectedBtn) {
        // Center only the chapter rail. scrollIntoView also scrolls page ancestors
        // and can move a live Pause/Stop button out from under the pointer.
        const rail = railListRef.current;
        const railRect = rail.getBoundingClientRect();
        const itemRect = selectedBtn.getBoundingClientRect();
        rail.scrollTo({
          left: rail.scrollLeft + itemRect.left - railRect.left - (rail.clientWidth - itemRect.width) / 2,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
        });
      }
    }, 60);
    return () => window.clearTimeout(timer);
  }, [selected, batchIndex]);

  // 点击外部关闭分卷选择下拉
  useEffect(() => {
    if (!volumeDropdownOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !volumeDropdownRef.current?.contains(event.target)) {
        setVolumeDropdownOpen(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [volumeDropdownOpen]);

  // 计算全书各批次
  const batchOptions = useMemo(() => {
    const options: Array<{ index: number; start: number; end: number; label: string; completed: number }> = [];
    for (let i = 0; i < totalBatches; i++) {
      const start = i * BATCH_SIZE + 1;
      const end = Math.min(totalChapters, (i + 1) * BATCH_SIZE);
      const slice = data.chapters.slice(start - 1, end);
      const completed = slice.filter((c) => c.status === "final").length;
      options.push({
        index: i,
        start,
        end,
        label: `第 ${start} - ${end} 章`,
        completed
      });
    }
    return options;
  }, [totalBatches, totalChapters, data.chapters]);

  const activeBatch = batchOptions[batchIndex] || batchOptions[0] || {
    index: 0,
    start: 1,
    end: Math.min(BATCH_SIZE, totalChapters),
    label: `第 1 - ${Math.min(BATCH_SIZE, totalChapters)} 章`,
    completed: 0
  };

  // 当前批次内的 50 个完整连续工位（彻底解决只能看 9 章的缺陷）
  const visibleChapters = useMemo(() => {
    if (!activeBatch) return data.chapters.slice(0, BATCH_SIZE);
    return data.chapters.slice(activeBatch.start - 1, activeBatch.end);
  }, [data.chapters, activeBatch]);

  // 兼容别名
  const nearby = visibleChapters;

  // 检查工位导轨左右滚动可用性（用于控制两翼渐变遮罩与微滑按钮）
  const checkRailScroll = () => {
    if (!railListRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = railListRef.current;
    setCanScrollLeft(scrollLeft > 6);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 6);
  };

  useEffect(() => {
    const el = railListRef.current;
    if (!el) return;
    checkRailScroll();
    el.addEventListener("scroll", checkRailScroll, { passive: true });
    window.addEventListener("resize", checkRailScroll);
    return () => {
      el.removeEventListener("scroll", checkRailScroll);
      window.removeEventListener("resize", checkRailScroll);
    };
  }, [visibleChapters, batchIndex]);

  // 平滑横向滚动步进
  const scrollRailBy = (delta: number) => {
    if (!railListRef.current) return;
    railListRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  // 鼠标滚轮直接横向平滑滚动
  const onRailWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (railListRef.current) {
      railListRef.current.scrollLeft += event.deltaY * 0.85;
    }
  };

  // 跳章表单提交
  const handleJumpSubmit = (event?: FormEvent) => {
    if (event) event.preventDefault();
    const target = Number(jumpInput.trim());
    if (/^\d+$/.test(jumpInput.trim()) && Number.isInteger(target) && target >= 1 && target <= totalChapters) {
      setSelected(target);
    } else {
      notify(`请输入 1–${totalChapters} 范围内的完整章节号`, "info");
      setJumpInput(String(selected));
    }
  };

  // 步进跳章（如 ±10）
  const handleStepChapter = (delta: number) => {
    const target = Math.max(1, Math.min(totalChapters, selected + delta));
    setSelected(target);
  };

  // 全景进度条点击跳跃
  const handleScrubberClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const target = Math.max(1, Math.min(totalChapters, Math.ceil(ratio * totalChapters)));
    setSelected(target);
  };

  const handleScrubberMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const chapter = Math.max(1, Math.min(totalChapters, Math.ceil(ratio * totalChapters)));
    setScrubberHover({ percent: ratio * 100, chapter });
  };

  // 全书目录矩阵检索过滤
  const matrixChapters = useMemo(() => {
    return data.chapters.filter((c) => {
      if (matrixFilter === "final" && c.status !== "final") return false;
      if (matrixFilter === "draft" && !["draft", "planned"].includes(c.status)) return false;
      if (matrixFilter === "ready" && c.status !== "ready") return false;
      if (matrixSearch.trim()) {
        const query = matrixSearch.trim().toLowerCase();
        return String(c.number).includes(query) || c.title.toLowerCase().includes(query);
      }
      return true;
    });
  }, [data.chapters, matrixFilter, matrixSearch]);
  useEffect(() => setMatrixPage(1), [matrixSearch, matrixFilter]);
  const matrixPages = Math.max(1, Math.ceil(matrixChapters.length / 100));
  const currentMatrixPage = Math.min(matrixPage, matrixPages);
  const visibleMatrixChapters = matrixChapters.slice((currentMatrixPage - 1) * 100, currentMatrixPage * 100);

  const runActive = Boolean(run && ["queued", "running", "paused", "needs_attention"].includes(run.status));
  const failedPlanExtension = data.planExtension?.status === "failed" ? data.planExtension : null;
  const planningActive = [data.planRebuild?.status, data.planExtension?.status].some((status) => status === "queued" || status === "running");
  const editorLocked = runActive || planningActive || saving || actionBusy || runStarting || feedbackBusy;
  const cardLocked = editorLocked || Boolean(detail?.draft || detail?.finalText);
  const contractLimits = [["次要推进", supportingProgressText, 4], ["压缩 / 省略", compressionNotesText, 3], ["硬结果", hardOutcomesText, 8], ["硬禁区", mustAvoidText, 12]] as const;
  const contractError = contractLimits.map(([label, value, limit]) => value.split(/\n+/).filter((line) => line.trim()).length > limit ? `${label}最多 ${limit} 项，请先合并或删减，不会自动截断` : "").find(Boolean) || (synopsisContent.trim().length < 80 ? `剧情软前提还需 ${80 - synopsisContent.trim().length} 字` : !coreEvent.trim() ? "请填写唯一主事件" : "");
  const runRangeValid = Number.isInteger(startChapter) && Number.isInteger(endChapter) && startChapter === data.project.currentChapter + 1 && endChapter >= startChapter && endChapter <= approvedPlanThroughValue(data.chapters);
  useSaveShortcut(() => tab === "card" ? saveSynopsis() : saveChapter(), !editorLocked && !loadingChapter && Boolean(detail) && (tab === "text" ? textDirty : tab === "card" && cardDirty && !cardLocked && !contractError));
  const inspectorChapter = inspectorRun && !runActive && selected >= inspectorRun.startChapter && selected <= inspectorRun.endChapter
    ? selected
    : inspectorRun?.currentChapter ?? selected;
  const hasNextChapter = data.project.currentChapter < data.project.targetChapters;
  const approvedPlanThrough = useMemo(() => {
    let through = 0;
    for (const chapter of data.chapters) {
      if (chapter.number !== through + 1 || chapter.synopsisStatus !== "ready") break;
      through = chapter.number;
    }
    return through;
  }, [data.chapters]);
  const nextChapterWritable = data.project.currentChapter + 1 <= approvedPlanThrough;
  const selectedCardWritable = Boolean(detail && detail.synopsisStatus === "ready" && detail.synopsis.trim().length >= 80 && detail.coreEvent.trim() && selected <= data.project.currentChapter + 1);
  const currentAgentIds = new Set(run?.status === "running" ? run.stages.find((stage) => stage.status === "running")?.agentIds ?? [] : []);
  const operationalEvents = useMemo(() => events.filter((event) => event.type !== "thinking"), [events]);
  const thinkingStreams = useMemo(() => {
    const streams = new Map<string, { key: string; agentId: string; label: string; stageLabel: string; text: string; timestamp: string }>();
    for (const event of events) {
      if (event.type !== "thinking" || event.chapter !== inspectorChapter || !event.message) continue;
      const agentId = event.agentId ?? "agent";
      const stageId = event.stageId ?? "stage";
      const key = `${stageId}:${agentId}`;
      const previous = streams.get(key);
      const agent = data.agents.find((item) => item.id === agentId);
      const stage = inspectorRun?.stages.find((item) => item.id === stageId);
      streams.set(key, {
        key,
        agentId,
        label: agent?.shortName ?? agent?.name ?? agentId,
        stageLabel: stage?.label ?? stageId,
        text: `${previous?.text ?? ""}${event.message}`.slice(-12_000),
        timestamp: event.timestamp
      });
    }
    return [...streams.values()].sort((left, right) => right.timestamp.localeCompare(left.timestamp));
  }, [events, inspectorChapter, inspectorRun?.id, data.agents]);
  const latestRound = scoreRounds.at(-1);
  const reviewScores = latestRound?.scores ?? (reviewRun?.currentChapter === selected ? reviewRun.scores : []);
  const reviewAggregate = latestRound?.aggregate ?? detail?.score ?? null;

  // 聚合计算 4 大核心里程碑状态
  const milestoneStatuses = useMemo(() => {
    if (!inspectorRun) return [];
    return PIPELINE_MILESTONES.map((group) => {
      const stages = group.stageIds
        .map((id) => inspectorRun.stages.find((s) => s.id === id))
        .filter((s): s is PipelineStage => Boolean(s));

      const hasRunning = stages.some((s) => s.status === "running");
      const hasFailed = stages.some((s) => s.status === "failed");
      const allPassed = stages.some((s) => s.status === "passed") && stages.every((s) => s.status === "passed" || s.status === "skipped");

      let status: "passed" | "running" | "failed" | "waiting" = "waiting";
      if (hasFailed) status = "failed";
      else if (hasRunning) status = "running";
      else if (allPassed) status = "passed";

      const activeStage = stages.find((s) => s.status === "running" || s.status === "failed") || stages[0];

      return {
        ...group,
        status,
        stages,
        activeStage
      };
    });
  }, [inspectorRun]);

  // 全局透视：创作流水线 4 大阶段与具体 Agent 完整步进状态（无论是运行中还是选中章节，每个 Agent 都可点可看）
  const effectiveMilestones = useMemo(() => {
    // Stage summaries belong to a specific run chapter. Final text alone is not evidence that every agent passed.
    const observed = inspectorRun?.currentChapter === inspectorChapter ? inspectorRun : null;
    const observedChapter = detail?.number === inspectorChapter ? detail : null;

    return PIPELINE_MILESTONES.map((group, groupIndex) => {
      const stages = observed
        ? group.stageIds
            .map((id) => observed.stages.find((s) => s.id === id))
            .filter((s): s is PipelineStage => Boolean(s))
        : [];

      const hasRunning = stages.some((s) => s.status === "running");
      const hasFailed = stages.some((s) => s.status === "failed");
      const allPassed = stages.some((s) => s.status === "passed") && stages.every((s) => s.status === "passed" || s.status === "skipped");

      let groupStatus: "passed" | "running" | "failed" | "waiting" = "waiting";
      if (hasFailed) groupStatus = "failed";
      else if (hasRunning && observed?.status === "running") groupStatus = "running";
      else if (allPassed) groupStatus = "passed";

      const activeStage = stages.find((s) => s.status === "running" || s.status === "failed") || stages[0];

      let shortStatusText = "等待";
      if (groupStatus === "passed") {
        shortStatusText = groupIndex === 0 ? "准备记录已完成" : groupIndex === 1 ? "写作记录已完成" : groupIndex === 2 ? (observedChapter?.score == null ? "阶段已完成 · 无评分" : `评分 ${observedChapter.score.toFixed(1)}`) : "终审阶段已完成";
      } else if (groupStatus === "running") {
        shortStatusText = activeStage?.label ?? "进行中";
      } else if (groupStatus === "failed") {
        shortStatusText = "异常需处理";
      } else {
        shortStatusText = !stages.length ? "暂无本章过程记录" : stages.every((stage) => stage.status === "skipped") ? "该组已跳过" : observed?.status === "paused" ? "任务已暂停" : "等待执行";
      }

      // 映射并生成该阶段下的所有具体 Agent 任务项（保证全部展示且支持点击看详细）
      const items: PipelineStepItem[] = group.stageIds.map((stageId) => {
        const meta = STAGE_AGENT_MAP[stageId] ?? { agentId: "writer", shortName: "Agent", defaultLabel: stageId };
        const runStage = observed?.stages.find((s) => s.id === stageId);

        let itemStatus: "passed" | "running" | "failed" | "waiting" = "waiting";
        let itemDetail = "等待";

        if (runStage) {
          itemStatus = runStage.status === "skipped" || (runStage.status === "running" && observed?.status !== "running") ? "waiting" : runStage.status;
          itemDetail = runStage.status === "skipped" ? "该阶段已跳过，不代表通过" : observed?.status === "paused" && runStage.status === "running" ? "任务已暂停" : runStage.detail || (runStage.status === "passed" ? "已完成" : runStage.status === "running" ? "执行中…" : "等待");
        } else {
          itemDetail = stageId === "write" && observedChapter?.words ? `已有正文 ${formatNumber(observedChapter.words)} 字；过程记录未载入` : stageId === "score" && observedChapter?.score != null ? `章节评分 ${observedChapter.score.toFixed(1)}；明细未载入` : "暂无本章执行记录，可查看 Agent 职责";
        }

        return {
          id: stageId,
          label: runStage?.label || meta.defaultLabel,
          agentId: meta.agentId,
          agentShortName: meta.shortName,
          status: itemStatus,
          detail: itemDetail
        };
      });

      return {
        ...group,
        status: groupStatus,
        stages,
        activeStage,
        shortStatusText,
        items
      };
    });
  }, [runActive, inspectorRun, detail, inspectorChapter]);

  const currentRunningMilestone = effectiveMilestones.find((m) => m.status === "running");
  const currentActiveStageName = currentRunningMilestone?.activeStage?.label ?? currentRunningMilestone?.title ?? "";
  const currentActiveAgent = data.agents.find((a) => currentAgentIds.has(a.id));
  const currentActiveAgentName = currentActiveAgent?.shortName ?? currentActiveAgent?.name ?? "";

  const stepperProgressPercent = useMemo(() => {
    const stages = effectiveMilestones.flatMap((milestone) => milestone.items);
    return stages.length ? stages.filter((stage) => stage.status === "passed").length / stages.length * 100 : 0;
  }, [effectiveMilestones]);

  // 实时记录分类过滤
  const filteredEvents = useMemo(() => {
    let list = operationalEvents;
    if (logFilter === "error") {
      list = list.filter((e) => e.level === "error" || e.type === "error" || /失败|中断|冲突|错误|未通过/.test(e.message));
    } else if (logFilter === "score") {
      list = list.filter((e) => e.type === "score" || /评分|观测分|五维|分档|门禁/.test(e.message));
    } else if (logFilter === "decision") {
      list = list.filter((e) => /重写|自愈|退回|修改|裁决|升级|返修/.test(e.message));
    }
    return list;
  }, [operationalEvents, logFilter]);

  // Position the stream before paint; a late scroll event must not fight the follow control.
  useLayoutEffect(() => {
    if (!autoScrollLogs) return;
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [filteredEvents.at(-1)?.id, logFilter, autoScrollLogs]);

  // Default to the current step, but never override this chapter's manual
  // disclosure choices when an initial fetch or background refresh completes.
  useEffect(() => {
    if (!detail || detail.number !== selected || manualMilestoneContext.current === `${selected}:${run?.id ?? "idle"}`) return;
    if (runActive) {
      const runningMilestone = effectiveMilestones.find((m) => m.status === "running")
        ?? effectiveMilestones.find((m) => m.status === "waiting")
        ?? effectiveMilestones[0];
      if (runningMilestone) {
        setExpandedMilestones(new Set([runningMilestone.id]));
      }
    } else {
      const chStatus = detail?.status ?? "draft";
      let activeTargetId = "prep";
      if (chStatus === "final") {
        activeTargetId = ""; // 定稿状态默认全部收起，清爽通透
      } else if (chStatus === "draft" && Boolean(detail?.draft || detail?.finalText || (detail?.words && detail.words > 0))) {
        activeTargetId = "review"; // 正文初稿完成，当前处于第 3 阶段（质检与返修）
      } else if (chStatus === "ready" || detail?.synopsisStatus === "ready") {
        activeTargetId = "draft"; // 章卡就绪，当前处于第 2 阶段（正文写作）
      } else {
        activeTargetId = "prep"; // 准备章卡
      }
      setExpandedMilestones(activeTargetId ? new Set([activeTargetId]) : new Set());
    }
  }, [runActive, run?.id, detail?.status, detail?.draft, detail?.finalText, selected]);

  const toggleMilestone = (id: string) => {
    manualMilestoneContext.current = `${selected}:${run?.id ?? "idle"}`;
    setExpandedMilestones((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  async function extendChapterPlans() {
    if (mutationLock.current || runActive || planningActive) return;
    if (dirty) { notify("请先保存当前修改，再规划后续章纲", "info"); return; }
    mutationLock.current = true;
    setPlanExtensionBusy(true);
    try {
      await postJson(`/api/projects/${data.project.id}/plan-extensions`, { count: 25 });
      notify("后续25章正在逐章生成并审查", "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "后续章纲启动失败", "error");
    } finally {
      mutationLock.current = false;
      setPlanExtensionBusy(false);
    }
  }

  async function resumeChapterPlans() {
    if (!failedPlanExtension || mutationLock.current || runActive || planningActive) return;
    if (dirty) { notify("请先保存当前修改，再继续章纲", "info"); return; }
    mutationLock.current = true;
    setPlanExtensionBusy(true);
    try {
      const result = await postJson<{ job: PlanExtensionJob }>(
        `/api/projects/${encodeURIComponent(data.project.id)}/plan-extensions/${encodeURIComponent(failedPlanExtension.id)}/resume`
      );
      notify(`已从第 ${result.job.currentChapter} 章继续生成后续章纲`, "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "后续章纲继续失败", "error");
    } finally {
      mutationLock.current = false;
      setPlanExtensionBusy(false);
    }
  }

  async function openStageDetail(stage: PipelineStage) {
    if (!inspectorRun) return;
    const sequence = ++stageSequence.current; setStageError("");
    const target = { stage, chapter: inspectorChapter };
    setStageDetailTarget(target);
    setStageDetail(null);
    setStageDetailBusy(true);
    try {
      const result = await api<{ detail: RunStageDetail | null }>(`/api/runs/${inspectorRun.id}/stages/${encodeURIComponent(stage.id)}?chapter=${target.chapter}`);
      if (sequence === stageSequence.current) setStageDetail(result.detail);
    } catch (error) {
      if (sequence === stageSequence.current) setStageError(error instanceof Error ? error.message : "阶段详情加载失败");
    } finally {
      if (sequence === stageSequence.current) setStageDetailBusy(false);
    }
  }

  async function openAgentOrStageDetail(item: PipelineStepItem) {
    const sequence = ++stageSequence.current; setStageError(""); setStageDetailBusy(false);
    const chapter = runActive && inspectorRun?.currentChapter ? inspectorRun.currentChapter : selected;
    const stage: PipelineStage = {
      id: item.id,
      label: item.label,
      status: item.status,
      detail: item.detail,
      agentIds: [item.agentId]
    };
    const target = { stage, chapter };
    setStageDetailTarget(target);
    setSelectedAgentInfo({
      agentId: item.agentId,
      agentName: item.label,
      stageId: item.id,
      stageLabel: item.label,
      status: item.status,
      chapter
    });
    setStageDetail(null);

    if (inspectorRun) {
      setStageDetailBusy(true);
      try {
        const result = await api<{ detail: RunStageDetail | null }>(
          `/api/runs/${inspectorRun.id}/stages/${encodeURIComponent(item.id)}?chapter=${chapter}`
        );
        if (sequence === stageSequence.current) setStageDetail(result.detail);
      } catch (error) {
        if (sequence === stageSequence.current) setStageError(error instanceof Error ? error.message : "执行记录读取失败，以下仅展示职责说明");
      } finally {
        if (sequence === stageSequence.current) setStageDetailBusy(false);
      }
    }
  }

  async function saveChapter() {
    if (!detail || editorLocked || loadingChapter || saveLock.current || !textDirty) return;
    if (cardDirty) { notify("请先保存简介章卡，再保存正文，避免正文锁定尚未保存的章卡", "info"); setTab("card"); return; }
    saveLock.current = true;
    if (detail.status === "final" && !await confirmAction({ title: "将定稿保存为草稿？", description: "本次编辑会创建历史快照，然后转为待复审草稿；当前评分和账本会重置。未重新评审前不再计为已定稿。", confirmLabel: "确认保存为草稿" })) { saveLock.current = false; return; }
    setSaving(true);
    try {
      const next = await putJson<ChapterDetail>(`/api/projects/${data.project.id}/chapters/${selected}`, { content, expectedVersion: detail.version });
      setDetail(next); setContent(next.draft || next.finalText);
      notify(`第 ${selected} 章草稿已保存`, "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "保存失败", "error");
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }

  async function saveSynopsis() {
    if (!detail || cardLocked || loadingChapter || saveLock.current || !cardDirty) return;
    if (contractError) { notify(contractError, "error"); return; }
    saveLock.current = true;
    setSaving(true);
    try {
      const next = await putJson<ChapterDetail>(`/api/projects/${data.project.id}/chapters/${selected}/synopsis`, {
        synopsis: synopsisContent.trim(),
        expectedVersion: detail.version,
        coreEvent: coreEvent.trim(),
        supportingProgress: contractLines(supportingProgressText, 4),
        compressionNotes: contractLines(compressionNotesText, 3),
        dramaticQuestion: dramaticQuestion.trim(),
        hardOutcomes: contractLines(hardOutcomesText, 8),
        endingState: endingState.trim(),
        mustAvoid: contractLines(mustAvoidText, 12),
        creativeFreedom: creativeFreedom.trim()
      });
      setDetail(next);
      setSynopsisContent(next.synopsis); setCoreEvent(next.coreEvent);
      setSupportingProgressText(next.supportingProgress.join("\n")); setCompressionNotesText(next.compressionNotes.join("\n"));
      setDramaticQuestion(next.dramaticQuestion); setHardOutcomesText(next.hardOutcomes.join("\n"));
      setEndingState(next.endingState); setMustAvoidText(next.mustAvoid.join("\n")); setCreativeFreedom(next.creativeFreedom ?? "");
      notify(`第 ${selected} 章逐章简介已保存`, "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "逐章简介保存失败", "error");
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }

  async function submitRun(event: FormEvent) {
    event.preventDefault();
    if (runLock.current || runActive || planningActive || !runRangeValid || !data.pi.authenticated) return;
    if (dirty) { notify("请先保存正文和章卡，再启动生成", "info"); return; }
    runLock.current = true; setRunStarting(true);
    try { await onStartRun(startChapter, endChapter); setRunDialog(false); }
    catch (error) { notify(error instanceof Error ? error.message : "启动失败，请先确认任务状态再重试", "error"); }
    finally { runLock.current = false; setRunStarting(false); }
  }

  async function controlRun(action: "pause" | "resume" | "stop") {
    if (controlLock.current) return; controlLock.current = true;
    try {
      if (action === "stop" && !await confirmAction({ title: "停止本次生成？", description: "停止请求会在服务端生效。已保存正文与历史版本会保留，尚未完成的生成可能需要重新启动。仅暂时离开时请选择暂停。", confirmLabel: "停止生成", danger: true })) return;
      setControlBusy(true); await onControlRun(action);
    } catch (error) { notify(error instanceof Error ? error.message : "任务控制失败", "error"); }
    finally { controlLock.current = false; setControlBusy(false); }
  }

  function openRunDialog() {
    if (dirty) { notify("请先保存正文和章卡，再启动生成", "info"); return; }
    if (runActive || planningActive || runStarting) return;
    const next = data.project.currentChapter + 1;
    setStartChapter(next);
    setEndChapter(next);
    setRunDialog(true);
  }

  async function loadUserRevisions(): Promise<ChapterUserRevision[]> {
    const result = await api<{ revisions: ChapterUserRevision[] }>(`/api/projects/${data.project.id}/chapters/${selected}/user-revisions`);
    if (selectedRef.current === selected) setUserRevisions(result.revisions);
    return result.revisions;
  }

  async function openFeedback() {
    if (feedbackLoadBusy) return;
    setActionMenu(false); setFeedbackOpen(true); setFeedbackLoadBusy(true); setFeedbackLoadError("");
    try { await loadUserRevisions(); }
    catch (error) { setFeedbackLoadError(error instanceof Error ? error.message : "修改意见加载失败"); }
    finally { setFeedbackLoadBusy(false); }
  }

  async function submitFeedback(event: FormEvent) {
    event.preventDefault();
    if (mutationLock.current || feedbackLoadBusy || feedbackLoadError || feedbackText.trim().length < 5 || activeUserRevision || editorLocked) return;
    if (dirty) { notify("请先保存当前正文和章卡，再提交修改意见", "info"); return; }
    mutationLock.current = true;
    setFeedbackBusy(true);
    try {
      const request = await postJson<ChapterUserRevision>(`/api/projects/${data.project.id}/chapters/${selected}/user-revisions`, { instruction: feedbackText });
      setUserRevisions((current) => [request, ...current]);
      setFeedbackText("");
      notify("作者修改意见已提交给新的写作 Agent", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "修改意见提交失败", "error");
    } finally { mutationLock.current = false; setFeedbackBusy(false); }
  }

  async function openHistory() {
    const sequence = ++historySequence.current;
    setRevisions([]); setHistoryError("");
    setActionMenu(false);
    setHistoryOpen(true);
    setHistoryBusy(true);
    try {
      const result = await api<{ revisions: ChapterRevision[] }>(`/api/projects/${data.project.id}/chapters/${selected}/revisions`);
      if (sequence === historySequence.current && selectedRef.current === selected) setRevisions(result.revisions);
    } catch (error) {
      if (sequence === historySequence.current) setHistoryError(error instanceof Error ? error.message : "版本历史加载失败");
    } finally {
      if (sequence === historySequence.current) setHistoryBusy(false);
    }
  }

  async function deleteChapter() {
    if (mutationLock.current || editorLocked || !detail) return;
    mutationLock.current = true;
    if (!await confirmDiscard(dirty, "本章未保存的修改")) { mutationLock.current = false; return; }
    setActionBusy(true);
    try {
      const next = await postJson<ChapterDetail>(`/api/projects/${data.project.id}/chapters/${selected}/actions/delete`);
      setDetail(next);
      setContent("");
      setChapterAction(null);
      notify(`第 ${selected} 章正文已删除，旧稿已进入版本历史`, "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "删除失败", "error");
    } finally {
      mutationLock.current = false;
      setActionBusy(false);
    }
  }

  async function rewriteChapter() {
    if (mutationLock.current || editorLocked || !detail || !selectedCardWritable) return;
    if (dirty) { notify("请先保存正文和章卡，再启动重写", "info"); return; }
    mutationLock.current = true;
    setActionBusy(true);
    try {
      await onRewriteChapter(selected);
      setDetail(null); setChapterReload((value) => value + 1);
      setContent("");
      setChapterAction(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : "重写启动失败", "error");
    } finally {
      mutationLock.current = false;
      setActionBusy(false);
    }
  }

  async function restoreRevision(revision: ChapterRevision) {
    if (mutationLock.current || editorLocked) return;
    mutationLock.current = true;
    if (!await confirmAction({ title: `恢复第 ${selected} 章版本 ${revision.version}？`, description: `将以该历史快照替换当前正文；当前已保存版本会进入历史记录。${dirty ? "当前未保存的修改会被放弃，请先复制需要保留的内容。" : ""}恢复后的定稿状态以服务端校验结果为准。`, confirmLabel: "确认恢复此版本", danger: true })) { mutationLock.current = false; return; }
    setActionBusy(true);
    try {
      const next = await postJson<ChapterDetail>(`/api/projects/${data.project.id}/chapters/${selected}/revisions/${revision.id}/restore`);
      setDetail(null); setContent(""); setChapterReload((value) => value + 1);
      setHistoryOpen(false);
      notify(`已恢复第 ${selected} 章版本 ${revision.version}`, "success");
      await onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "版本恢复失败", "error");
    } finally {
      mutationLock.current = false;
      setActionBusy(false);
    }
  }

  return (
    <div className="workspace-page">
      {/* 顶部项目状态与控制中枢（一体化整合，章卡任务条内嵌） */}
      <section className="project-strip panel-section">
        <div className="project-strip-body">
          <div className="project-strip-cover"><BookCover project={data.project} /></div>
          <div className="project-strip-main">
            <div className="eyebrow-row">
              <span className="volume-tag">{data.project.currentVolume}</span>
              <span className="dot-separator" />
              <span className="project-status-pill">{data.project.status}</span>
            </div>
            <h1>{data.project.title}</h1>
            <div className="project-metrics">
              <span>已完成 <strong>{data.project.completedChapters}</strong> / {formatNumber(data.project.targetChapters)} 章</span>
              <span className="dot-separator" />
              <span>{relativeTime(data.project.lastUpdated)}更新</span>
            </div>
          </div>

          {/* 关键项目统计指标：优雅内嵌，消除平铺割裂感 */}
          <div className="workspace-metrics inline-metrics" aria-label="项目指标">
            <article>
              <strong>{data.project.completedChapters}</strong>
              <span>已定稿章节</span>
            </article>
            <article>
              <strong>{formatNumber(data.project.wordCount)}</strong>
              <span>累计字数</span>
            </article>
            <article>
              <strong>{data.quality.overall === null ? "—" : data.quality.overall.toFixed(1)}</strong>
              <span>综合评分</span>
            </article>
            <article>
              <strong>{approvedPlanThrough}</strong>
              <span>章卡已通过</span>
            </article>
          </div>

          <div className="project-strip-run">
            <div className="runtime-state">
              <StatusDot status={data.pi.authenticated ? "running" : "warning"} />
              <span>{data.pi.authenticated ? "Pi 已就绪" : "模型未连接"}</span>
            </div>
            <div className="project-strip-actions">
              <button className="button button-secondary" type="button" onClick={() => onOpenReader(selected)} disabled={loadingChapter || !detail || !content.trim()}>
                <BookOpenText size={16} />阅读模式
              </button>
              {!runActive ? (
                failedPlanExtension ? (
                  <button className="button button-primary" type="button" onClick={() => { void resumeChapterPlans(); }} disabled={!data.pi.authenticated || planExtensionBusy || runActive || planningActive}>
                    {planExtensionBusy ? <span className="spinner" /> : <Play size={16} fill="currentColor" />}从第 {failedPlanExtension.currentChapter} 章继续章纲
                  </button>
                ) : nextChapterWritable ? (
                  <button className="button button-primary" type="button" onClick={openRunDialog} disabled={!data.pi.authenticated || !hasNextChapter || planningActive || runStarting || planExtensionBusy || saving || actionBusy || feedbackBusy} title={!data.pi.authenticated ? "请先在 Agent 工作室连接模型" : planningActive ? "章卡规划任务尚未完成" : `第${data.project.currentChapter + 1}章章卡已通过审查`}>
                    <Play size={16} fill="currentColor" />开始生成
                  </button>
                ) : (
                  <button className="button button-primary" type="button" onClick={() => { void extendChapterPlans(); }} disabled={!data.pi.authenticated || !hasNextChapter || planExtensionBusy || ["queued", "running"].includes(data.planExtension?.status ?? "")}>
                    {planExtensionBusy || ["queued", "running"].includes(data.planExtension?.status ?? "") ? <span className="spinner" /> : <ListChecks size={16} />}生成后续章纲
                  </button>
                )
              ) : (
                <div className="run-controls">
                  {run?.status === "paused" || run?.status === "needs_attention" ? (
                    <button className="button button-primary" type="button" disabled={controlBusy} onClick={() => void controlRun("resume")}>
                      <Play size={16} />继续
                    </button>
                  ) : (
                    <button className="button button-secondary" type="button" disabled={controlBusy} onClick={() => void controlRun("pause")}>
                      <Pause size={16} />暂停
                    </button>
                  )}
                  <button className="icon-button danger" type="button" title="停止" disabled={controlBusy} aria-label="停止生成" onClick={() => void controlRun("stop")}>
                    <Square size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 章卡规划/滚动生成任务条：内嵌在项目头栏底部，一体成型，绝不横向割裂页面 */}
        {data.planExtension && data.planExtension.status !== "completed" && (
          <div className={`plan-rebuild-banner plan-rebuild-${data.planExtension.status}`}>
            <div className="plan-rebuild-main-row">
              <span className={data.planExtension.status === "failed" ? "status-dot status-failed" : "spinner dark"} />
              <div className="plan-rebuild-texts">
                <strong>{data.planExtension.status === "failed" ? "章卡规划中断" : "滚动生成章纲中"}</strong>
                <small title={data.planExtension.error || data.planExtension.detail}>
                  第 {data.planExtension.currentChapter} 章 · {data.planExtension.error || data.planExtension.detail}
                </small>
              </div>
              <div className="plan-rebuild-meta-ops">
                <em>{data.planExtension.progress}%</em>
                {data.planExtension.status === "failed" && (
                  <button className="button button-primary plan-extension-resume" type="button" onClick={() => { void resumeChapterPlans(); }} disabled={!data.pi.authenticated || planExtensionBusy || runActive || planningActive}>
                    {planExtensionBusy ? <span className="spinner" /> : <Play size={13} fill="currentColor" />}从第 {data.planExtension.currentChapter} 章继续
                  </button>
                )}
              </div>
            </div>
            <div className="plan-rebuild-progress">
              <span style={{ width: `${data.planExtension.progress}%` }} />
            </div>
          </div>
        )}

        {data.planRebuild && data.planRebuild.status !== "completed" && (
          <div className={`plan-rebuild-banner plan-rebuild-${data.planRebuild.status}`}>
            <div className="plan-rebuild-main-row">
              <span className={data.planRebuild.status === "failed" ? "status-dot status-failed" : "spinner dark"} />
              <div className="plan-rebuild-texts">
                <strong>{data.planRebuild.status === "failed" ? "章卡重建失败，需要检查" : `第1–${data.planRebuild.totalPlans}章简介重新设计中`}</strong>
                <small title={data.planRebuild.error || data.planRebuild.detail}>
                  {data.planRebuild.error || data.planRebuild.detail}
                </small>
              </div>
              <div className="plan-rebuild-meta-ops">
                <em>{data.planRebuild.progress}%</em><button type="button" className="button button-secondary" onClick={() => setRebuildHistoryOpen(true)}>查看重建记录</button>
              </div>
            </div>
            <div className="plan-rebuild-progress">
              <span style={{ width: `${data.planRebuild.progress}%` }} />
            </div>
          </div>
        )}

        <div className="project-progress">
          <span style={{ width: `${Math.max(0.3, (data.project.completedChapters / data.project.targetChapters) * 100)}%` }} />
        </div>
      </section>

      {/* 工作台主栅格：左侧一体化创作主舞台 + 右侧生成指挥台中枢 */}
      <div className="workspace-grid">
        <div className="workspace-main-column panel-section atelier-stage">
          {/* 上部：章节流水线导轨（支持多尺度导航、全景擦拭与50章连续平滑滚动） */}
          <aside className="chapter-rail">
            <div className="section-heading rail-heading">
              <div className="rail-heading-group">
                <span className="rail-pulse-beacon" />
                <h2>创作流水线</h2>

                {/* 批次/分卷快速切换下拉 */}
                <div className="rail-volume-selector" ref={volumeDropdownRef}>
                  <button
                    type="button"
                    className="rail-volume-trigger"
                    onClick={() => setVolumeDropdownOpen(!volumeDropdownOpen)}
                    title="切换章节区间"
                    aria-haspopup="menu" aria-controls="rail-volume-menu" aria-expanded={volumeDropdownOpen}
                    onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); setVolumeDropdownOpen(true); } }}
                  >
                    <span>{activeBatch.label}</span>
                    <ChevronDown size={13} className={`chevron-icon ${volumeDropdownOpen ? "open" : ""}`} />
                  </button>

                  {volumeDropdownOpen && (
                    <div className="rail-volume-dropdown" id="rail-volume-menu" role="menu" aria-label="章节区间">
                      <div className="rail-volume-dropdown-header">
                        <span>全书 {formatNumber(totalChapters)} 章 · 跨度选择</span>
                      </div>
                      <div className="rail-volume-dropdown-list">
                        {batchOptions.map((b) => (
                          <button
                            key={b.index}
                            type="button"
                            className={`volume-batch-item ${b.index === batchIndex ? "selected" : ""}`}
                            role="menuitem"
                            onClick={async () => {
                              if (await setSelected(selected >= b.start && selected <= b.end ? selected : b.start)) {
                                setBatchIndex(b.index); setVolumeDropdownOpen(false);
                              }
                            }}
                          >
                            <span className="batch-label">{b.label}</span>
                            <span className="batch-meta">
                              {b.completed > 0 ? (
                                <span className="batch-completed">{b.completed} 定稿</span>
                              ) : (
                                <span className="batch-empty">待动笔</span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <span className="rail-stats-badge">
                  已定稿 <strong>{data.project.completedChapters}</strong> / {formatNumber(totalChapters)} 章
                </span>
              </div>

              {/* 中段：全景微型刻度擦拭条 (Horizon Scrubber) */}
              <div
                className="rail-scrubber"
                onClick={handleScrubberClick}
                onMouseMove={handleScrubberMouseMove}
                onMouseLeave={() => setScrubberHover(null)}
                role="slider" tabIndex={0} aria-label="全书章节快速跳转"
                aria-valuemin={1} aria-valuemax={totalChapters} aria-valuenow={selected} aria-valuetext={`第 ${selected} 章`}
                onKeyDown={(event) => {
                  if (event.nativeEvent.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
                  const offset = ({ ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageUp: -10, PageDown: 10 } as Record<string, number>)[event.key];
                  if (offset !== undefined || event.key === "Home" || event.key === "End") { event.preventDefault(); if (event.key === "Home") void setSelected(1); else if (event.key === "End") void setSelected(totalChapters); else handleStepChapter(offset); }
                }}
                title="点击或用方向键跳章；PageUp / PageDown 跳 10 章"
              >
                <span className="scrubber-bound-label start">1</span>
                <div className="scrubber-track">
                  <div
                    className="scrubber-fill-completed"
                    style={{ width: `${(data.project.completedChapters / totalChapters) * 100}%` }}
                  />
                  {run?.currentChapter && (
                    <div
                      className="scrubber-active-beacon"
                      style={{ left: `${(run.currentChapter / totalChapters) * 100}%` }}
                    />
                  )}
                  <div
                    className="scrubber-window-range"
                    style={{
                      left: `${((activeBatch.start - 1) / totalChapters) * 100}%`,
                      width: `${((activeBatch.end - activeBatch.start + 1) / totalChapters) * 100}%`
                    }}
                  />
                  <div
                    className="scrubber-thumb"
                    style={{ left: `${(selected / totalChapters) * 100}%` }}
                  />
                  {/* 分批刻度指示点 */}
                  <div className="scrubber-ticks" aria-hidden="true">
                    {batchOptions.slice(1).map((b) => (
                      <span
                        key={b.index}
                        className="scrubber-tick"
                        style={{ left: `${((b.start - 1) / totalChapters) * 100}%` }}
                      />
                    ))}
                  </div>
                </div>
                <span className="scrubber-bound-label end">{formatNumber(totalChapters)}</span>

                {scrubberHover && (
                  <div className="scrubber-tooltip" style={{ left: `${Math.min(90, Math.max(10, scrubberHover.percent))}%` }}>
                    <div className="scrubber-tooltip-inner">
                      <div className="tooltip-head">
                        <span className="tooltip-ch">第 {scrubberHover.chapter} 章</span>
                        {hoveredChapterData?.score !== null && hoveredChapterData?.score !== undefined ? (
                          <span className="tooltip-score">{hoveredChapterData.score.toFixed(1)} 分</span>
                        ) : hoveredChapterData?.status === "final" ? (
                          <span className="tooltip-pill final">已定稿</span>
                        ) : hoveredChapterData?.status === "ready" ? (
                          <span className="tooltip-pill ready">可开写</span>
                        ) : (
                          <span className="tooltip-pill draft">草稿</span>
                        )}
                      </div>
                      {hoveredChapterData?.title && (
                        <div className="tooltip-title">{hoveredChapterData.title}</div>
                      )}
                    </div>
                    <div className="scrubber-tooltip-arrow" />
                  </div>
                )}
              </div>

              {/* 右侧：步进跳章、快捷输入与全景目录 */}
              <div className="rail-controls">
                {run?.currentChapter && run.currentChapter !== selected && (
                  <button
                    type="button"
                    className="rail-jump-live"
                    onClick={() => setSelected(run.currentChapter ?? selected)}
                    title="跳转至当前生成章节"
                  >
                    <span className="pulse-dot" />第 {run.currentChapter} 章
                  </button>
                )}

                <div className="rail-nav-pager">
                  <button
                    type="button"
                    className="rail-nav-btn rail-step-btn"
                    onClick={() => handleStepChapter(-10)}
                    disabled={selected <= 1}
                    title="后退 10 章"
                  >
                    -10
                  </button>
                  <button
                    type="button"
                    className="rail-nav-btn"
                    onClick={() => setSelected(Math.max(1, selected - 1))}
                    disabled={selected <= 1}
                    title="上一章"
                  >
                    <ChevronLeft size={14} />
                  </button>

                  {/* 精准跳章表单 */}
                  <form className="rail-jump-form" onSubmit={handleJumpSubmit}>
                    <input
                      type="text"
                      className="rail-jump-input"
                      value={jumpInput}
                      onChange={(e) => setJumpInput(e.target.value)}
                      inputMode="numeric" pattern="[0-9]+" aria-label="跳转章节号"
                      title="输入章节号后按回车或点击跳转；移开焦点不会自动跳章"
                    />
                    <span className="rail-jump-total">/ {totalChapters}</span>
                    <button type="submit" className="rail-nav-btn" title="跳转章节" aria-label="跳转章节"><ChevronRight size={13} /></button>
                  </form>

                  <button
                    type="button"
                    className="rail-nav-btn"
                    onClick={() => setSelected(Math.min(totalChapters, selected + 1))}
                    disabled={selected >= totalChapters}
                    title="下一章"
                  >
                    <ChevronRight size={14} />
                  </button>
                  <button
                    type="button"
                    className="rail-nav-btn rail-step-btn"
                    onClick={() => handleStepChapter(10)}
                    disabled={selected >= totalChapters}
                    title="前进 10 章"
                  >
                    +10
                  </button>
                </div>

                {/* 全书目录矩阵按钮 */}
                <button
                  type="button"
                  className="rail-matrix-btn"
                  onClick={() => setMatrixOpen(true)}
                  title={`查看全书 ${formatNumber(totalChapters)} 章目录`}
                >
                  <Grid size={13} />
                  <span>目录</span>
                </button>
              </div>
            </div>

            {/* 连续流动式章节工位轨线（支持滚轮平滑横滑，连续展示当前批次 50 章） */}
            <div className="chapter-rail-scroll-wrapper">
              {/* 左边缘淡出渐变遮罩与快捷微滑按钮 */}
              <div className={`rail-edge-mask left ${canScrollLeft ? "visible" : ""}`}>
                <button
                  type="button"
                  className="rail-scroll-arrow left"
                  onClick={() => scrollRailBy(-320)}
                  aria-label="向左滚动章节"
                  tabIndex={-1}
                >
                  <ChevronLeft size={15} />
                </button>
              </div>

              <div
                className="chapter-rail-list"
                ref={railListRef}
                onWheel={onRailWheel}
              >
                <div className="pipeline-backbone-track" />
                {visibleChapters.map((chapter) => {
                  const isCurrentWriting = run?.currentChapter === chapter.number && runActive;
                  const isSelected = selected === chapter.number;
                  return (
                    <button
                      key={chapter.number}
                      type="button"
                      aria-current={isSelected ? "true" : undefined} className={`pipeline-station ${isSelected ? "selected" : ""} ${isCurrentWriting ? "writing" : ""}`}
                      onClick={() => setSelected(chapter.number)}
                      title={`第 ${chapter.number} 章: ${chapter.title}`}
                    >
                      {/* 工位头部：节点标示与评级/状态 */}
                      <div className="station-top">
                        <div className="station-node-group">
                          <span className={`station-node node-${chapter.status} ${isCurrentWriting ? "node-pulse" : ""}`}>
                            {chapter.status === "final" ? (
                              <Check size={9} strokeWidth={3} />
                            ) : (
                              <span className="node-pip" />
                            )}
                          </span>
                          <span className="station-chapter-num">第 {chapter.number} 章</span>
                        </div>
                        {chapter.score !== null ? (
                          <span className={`station-score-badge ${chapter.score >= 8.5 ? "score-high" : chapter.score >= 8.0 ? "score-good" : ""}`} title={`综合评分 ${chapter.score}`}>
                            {chapter.score.toFixed(1)}
                          </span>
                        ) : (
                          <span className={`station-status-pill pill-${isCurrentWriting ? "writing" : chapter.status}`}>
                            {isCurrentWriting ? run?.status === "running" ? "生成中" : runStatusLabel(run!.status) : chapterStatusLabel(chapter.status)}
                          </span>
                        )}
                      </div>

                      {/* 工位正文：章节标题 */}
                      <div className="station-body">
                        <strong className="station-title">{chapter.title}</strong>
                        {chapter.status !== "final" && chapter.synopsisStatus !== "missing" && <small className="chapter-title-state">暂定章名 · 待正文复核</small>}
                      </div>

                      {/* 工位底栏：字数与定稿/预警提示 */}
                      <div className="station-bottom">
                        <span className="station-words">
                          {chapter.words > 0 ? `${formatNumber(chapter.words)} 字` : "待动笔"}
                        </span>
                        {chapter.synopsisStatus === "needs_review" ? (
                          <span className="station-flag warning">旧章卡</span>
                        ) : (
                          <span className={`station-status-text text-${chapter.status}`}>
                            {chapterStatusLabel(chapter.status)}
                          </span>
                        )}
                      </div>

                      {/* 工位底边指示器与生产流动光 */}
                      <span className="station-baseline" />
                      {isCurrentWriting && <span className="station-writing-shimmer" />}
                    </button>
                  );
                })}
              </div>

              {/* 右边缘淡出渐变遮罩与快捷微滑按钮 */}
              <div className={`rail-edge-mask right ${canScrollRight ? "visible" : ""}`}>
                <button
                  type="button"
                  className="rail-scroll-arrow right"
                  onClick={() => scrollRailBy(320)}
                  aria-label="向右滚动章节"
                  tabIndex={-1}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </aside>

          {/* 下部：核心主编辑器画布 */}
          <main className="editor-panel">
            <header className="editor-header">
              <div className="editor-header-left">
                <span className="section-kicker">第 {selected} 章</span>
                <h2>{detail?.title ?? "加载中"}</h2>
                <span className={`chapter-status-tag status-${detail?.status ?? "draft"}`}>
                  {chapterStatusLabel(detail?.status)}
                </span>
              </div>

              <div className="editor-header-center">
                <div className="editor-tabs" role="tablist" aria-label="章节编辑面板" onKeyDown={choiceKeyDown}>
                  <button type="button" role="tab" id="editor-tab-text" aria-controls="editor-panel-text" aria-selected={tab === "text"} tabIndex={tab === "text" ? 0 : -1} className={tab === "text" ? "active" : ""} onClick={() => setTab("text")}>
                    <FileText size={15} />正文
                  </button>
                  <button type="button" role="tab" id="editor-tab-card" aria-controls="editor-panel-card" aria-selected={tab === "card"} tabIndex={tab === "card" ? 0 : -1} className={tab === "card" ? "active" : ""} onClick={() => setTab("card")}>
                    <BookOpenText size={15} />简介章卡
                  </button>
                  <button type="button" role="tab" id="editor-tab-review" aria-controls="editor-panel-review" aria-selected={tab === "review"} tabIndex={tab === "review" ? 0 : -1} className={tab === "review" ? "active" : ""} onClick={() => setTab("review")}>
                    <Gauge size={15} />分维度评分
                  </button>
                </div>
              </div>

              <div className="editor-header-right">
                {tab === "text" && (
                  <button className="button button-secondary editor-save-button" type="button" onClick={saveChapter} disabled={editorLocked || loadingChapter || !detail || !textDirty} title="保存当前草稿 (Ctrl+S / ⌘S)">
                    <Save size={15} />
                    <span>{saving ? "保存中" : "保存"}</span>
                  </button>
                )}
                <div className="chapter-action-menu" ref={actionMenuRef}>
                  <button
                    className="button button-secondary chapter-action-trigger"
                    type="button"
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      setTriggerRect(rect);
                      setActionMenu((value) => !value);
                    }}
                    aria-expanded={actionMenu} aria-haspopup="menu" aria-controls="chapter-action-menu"
                    onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); setTriggerRect(event.currentTarget.getBoundingClientRect()); setActionMenu(true); } }}
                    title="章节操作"
                  >
                    <MoreHorizontal size={16} />
                    <span className="desktop-action-label">章节操作</span>
                  </button>
                  {actionMenu && triggerRect && typeof document !== "undefined" && createPortal(
                    <div className="chapter-action-portal">
                      <div className="chapter-action-backdrop" aria-hidden="true" onClick={() => setActionMenu(false)} />
                      <div
                        className="chapter-action-popover" id="chapter-action-menu"
                        role="menu" aria-label="章节操作"
                        style={window.innerWidth > 700 ? {
                          position: "fixed",
                          top: Math.max(16, Math.min(triggerRect.bottom + 8, window.innerHeight - 320)),
                          right: Math.max(12, window.innerWidth - triggerRect.right),
                          zIndex: 1050
                        } : {
                          position: "fixed",
                          bottom: "max(16px, env(safe-area-inset-bottom, 16px))",
                          left: 12,
                          right: 12,
                          width: "auto",
                          zIndex: 1050
                        }}
                      >
                        <button
                          type="button" role="menuitem"
                          disabled={editorLocked || loadingChapter || !detail || (!detail.draft && !detail.finalText)}
                          onClick={openFeedback}
                          title={runActive ? "运行进行中已锁定" : !detail || (!detail.draft && !detail.finalText) ? "本章尚无正文" : "提交作者修改意见"}
                        >
                          <MessageSquarePlus size={15} />
                          <span>
                            <strong>提交修改意见</strong>
                            <small>按要求定向修改正文</small>
                          </span>
                        </button>
                        <button type="button" role="menuitem" onClick={openHistory}>
                          <History size={15} />
                          <span>
                            <strong>版本历史</strong>
                            <small>查看与恢复旧稿</small>
                          </span>
                        </button>
                        <button
                          type="button" role="menuitem"
                          disabled={editorLocked || loadingChapter || !selectedCardWritable}
                          onClick={() => { setActionMenu(false); setChapterAction("rewrite"); }}
                          title={runActive ? "运行进行中已锁定" : !selectedCardWritable ? "需等待章卡通过并按顺序写作" : "开始重写本章"}
                        >
                          <RotateCcw size={15} />
                          <span>
                            <strong>重写本章</strong>
                            <small>重新创建写作 Agent</small>
                          </span>
                        </button>
                        <button
                          className="danger"
                          type="button" role="menuitem"
                          disabled={editorLocked || loadingChapter || !detail || (!detail.draft && !detail.finalText)}
                          onClick={() => { setActionMenu(false); setChapterAction("delete"); }}
                          title={runActive ? "运行进行中已锁定" : !detail || (!detail.draft && !detail.finalText) ? "本章尚无正文" : "删除本章正文"}
                        >
                          <Trash2 size={15} />
                          <span>
                            <strong>删除正文</strong>
                            <small>保留章卡与历史</small>
                          </span>
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            </header>

            {editorLocked && <p className="ux-note" role="status">{saving ? "正在保存，请勿重复提交。" : "项目有运行或规划任务，编辑暂时只读；现有内容仍可选中复制。"}</p>}
            {(["text", "card", "review"] as const).filter((value) => value !== tab).map((value) => <div key={value} hidden role="tabpanel" id={`editor-panel-${value}`} aria-labelledby={`editor-tab-${value}`} />)}
            <div className="editor-tabpanel" role="tabpanel" id={`editor-panel-${tab}`} aria-labelledby={`editor-tab-${tab}`} tabIndex={0}>
            {loadingChapter ? (
              <div className="editor-loading" role="status"><span className="spinner dark" /><span>正在加载第 {selected} 章</span></div>
            ) : chapterError || !detail ? (
              <div className="ux-error" role="alert"><p>{chapterError || "本章内容暂不可用"}</p><button className="button button-secondary" type="button" onClick={() => setChapterReload((value) => value + 1)}>重试加载本章</button></div>
            ) : tab === "text" ? (
              <textarea
                className="chapter-editor"
                aria-label={`第 ${selected} 章正文`}
                readOnly={editorLocked}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                spellCheck={false}
                placeholder="本章正文内容... 聚焦情节叙事，Writer 将严格遵从章卡与正典设定"
              />
            ) : tab === "card" ? (
              <div className="chapter-card-view synopsis-only-view">
                <PlannedChapterTitleReceipt chapter={detail} />
                <p className="ux-note" role="status">{cardLocked ? "本章已有正文或项目有任务，章卡只读。修改正典请使用整书修订，或按既有章卡重写正文。" : contractError || "章卡要求已满足。Ctrl+S / ⌘S 保存；每行一项，超限不会截断。"}</p>
                {detail?.synopsisStatus === "needs_review" && (
                  <div className="legacy-card-warning">
                    <AlertTriangle size={17} />
                    <div>
                      <strong>正典已更新，待重生成章卡</strong>
                      <span>整书正典已修订，审查通过前不可进入正文流水线。</span>
                    </div>
                  </div>
                )}
                {detail?.bodyRevisionRequired && (
                  <div className="legacy-card-warning">
                    <AlertTriangle size={17} />
                    <div>
                      <strong>新章卡已通过，正文待重写</strong>
                      <span>旧稿已留存历史；按新章卡重写并通过门禁后此提示清除。</span>
                    </div>
                  </div>
                )}
                <section className="synopsis-editor-section">
                  <header>
                    <div>
                      <span>剧情软前提</span>
                      <small>
                        {detail?.synopsisStatus === "needs_review"
                          ? "旧版待替换"
                          : detail?.synopsisStatus === "ready" && synopsisContent.trim().length >= 80
                          ? "已就绪"
                          : "待完善"}
                      </small>
                    </div>
                    <button className="button button-secondary" type="button" onClick={saveSynopsis} disabled={cardLocked || Boolean(contractError) || !cardDirty} title={contractError || (cardLocked ? "章卡只读" : "保存当前章卡")}>
                      <Save size={14} />保存合同
                    </button>
                  </header>
                  <textarea
                    aria-label="剧情软前提"
                    readOnly={cardLocked}
                    maxLength={30000}
                    value={synopsisContent}
                    onChange={(event) => setSynopsisContent(event.target.value)}
                    placeholder="人物处境、核心主事件与变化方向"
                  />
                </section>
                <section className="chapter-contract-grid">
                  <label className="contract-field contract-field-wide">
                    <span>唯一主事件</span>
                    <input readOnly={cardLocked} value={coreEvent} onChange={(event) => setCoreEvent(event.target.value)} maxLength={240} placeholder="本章冲突、选择或不可逆变化的核心事件" />
                  </label>
                  <label className="contract-field">
                    <span>次要推进</span>
                    <textarea readOnly={cardLocked} value={supportingProgressText} onChange={(event) => setSupportingProgressText(event.target.value)} placeholder="可选次要变化，可独立成场景（最多4项）" />
                  </label>
                  <label className="contract-field">
                    <span>压缩 / 省略</span>
                    <textarea readOnly={cardLocked} value={compressionNotesText} onChange={(event) => setCompressionNotesText(event.target.value)} placeholder="禁止展开描写的背景或过渡（最多3项）" />
                  </label>
                  <label className="contract-field contract-field-wide">
                    <span>戏剧问题</span>
                    <input readOnly={cardLocked} value={dramaticQuestion} onChange={(event) => setDramaticQuestion(event.target.value)} maxLength={240} placeholder="本章悬而未决的核心矛盾或疑问" />
                  </label>
                  <label className="contract-field">
                    <span>硬结果</span>
                    <textarea readOnly={cardLocked} value={hardOutcomesText} onChange={(event) => setHardOutcomesText(event.target.value)} placeholder="后文真正依赖的事实，通常少量即可（最多8项）" />
                  </label>
                  <label className="contract-field">
                    <span>硬禁区</span>
                    <textarea readOnly={cardLocked} value={mustAvoidText} onChange={(event) => setMustAvoidText(event.target.value)} placeholder="真正不能突破的事实与信息边界（最多12项）" />
                  </label>
                  <label className="contract-field contract-field-wide">
                    <span>结束状态</span>
                    <input readOnly={cardLocked} value={endingState} onChange={(event) => setEndingState(event.target.value)} maxLength={600} placeholder="本章移交下一章的唯一新状态" />
                  </label>
                  <label className="contract-field contract-field-wide">
                    <span>创作自由</span>
                    <textarea readOnly={cardLocked} value={creativeFreedom} onChange={(event) => setCreativeFreedom(event.target.value)} maxLength={1000} placeholder="场景载体、验证方式、对白细节由 Writer 决定" />
                  </label>
                </section>
              </div>
            ) : (
              <div className="review-view">
                {scoreRoundsError && <div className="ux-error" role="alert"><p>{scoreRoundsError}。旧轮次已清除，不代表本章没有评分。</p><button className="button button-secondary" type="button" onClick={() => setScoreReload((value) => value + 1)}>重新读取评分记录</button></div>}
                <div className="review-score-large">
                  <span>{reviewAggregate?.toFixed(1) ?? "--"}</span>
                  <small>{latestRound ? `第 ${latestRound.round} 轮综合` : "终稿综合"}</small>
                </div>
                {reviewScores.length ? reviewScores.map((score) => (
                  <div className="review-row" key={score.agentId}>
                    <span>{score.dimension}</span>
                    <div><i style={{ width: `${score.score * 10}%` }} /></div>
                    <strong>{score.score.toFixed(1)}</strong>
                  </div>
                )) : !scoreRoundsBusy && (
                  detail?.score !== null && detail?.score !== undefined ? (
                    <div className="review-final-summary">
                      <p>第 {selected} 章记录的综合评分为 <strong>{detail.score.toFixed(1)}</strong> 分。当前没有载入对应评分轮次，不能据此推断所有维度或门禁均已通过。</p>
                    </div>
                  ) : (
                    <EmptyState icon={<Gauge size={22} />} title="暂无本章评分" detail="正文生成或评审完成后，将在此展示分维度评分与修改记录。" />
                  )
                )}
                {scoreRoundsBusy && <div className="review-loading"><span className="spinner dark" /></div>}
                {scoreRounds.length > 0 && (
                  <ScoreRoundHistory
                    rounds={scoreRounds}
                    publishThreshold={data.settings.scoreThreshold}
                    expanded={expandedRound}
                    onToggle={(round) => setExpandedRound((current) => (current === round ? null : round))}
                  />
                )}
              </div>
            )}

            </div>
            <footer className="editor-status">
              <span className="editor-word-count">
                <strong>{formatNumber(content.replace(/\s/g, "").length)}</strong> 字符
              </span>
              <span>版本 {detail?.version ?? "—"}</span>
              <span role="status">{saving ? "保存中…" : dirty ? "有未保存修改" : detail ? "已与载入版本同步" : "尚未载入"}</span>
              <span className={`status-text-${detail?.status ?? "draft"}`}>
                {chapterStatusLabel(detail?.status)}
              </span>
            </footer>
          </main>
        </div>

        {/* 右侧：生成指挥台中枢与实时记录一体化控制台 */}
        <aside className="run-inspector panel-section">
          {run?.status === "needs_attention" && run.error?.startsWith("[MODEL_INPUT_BUDGET]") &&
            <InputBudgetRecoveryPanel key={run.id} run={run} agents={data.agents} models={data.models} contextBudget={data.settings.contextBudget} profile={data.settings.contextBudgetProfile} onChanged={onChanged} />}
          {/* 指挥台顶舱 Header */}
          <div className="section-heading command-heading">
            <div className="command-header-title">
              <span className="section-kicker">COMMAND & LOGS</span>
              <h2>生成指挥台</h2>
            </div>
            <span className={`run-status-label run-${inspectorRun?.status ?? "idle"}`}>
              <StatusDot status={inspectorRun?.status ?? "stopped"} />
              {inspectorRun ? `${runStatusLabel(inspectorRun.status)} · ${runElapsed(inspectorRun.createdAt)}` : "待命"}
            </span>
          </div>

          <AgentFlowLauncher reviewWorkflow={data.settings.reviewWorkflow ?? "streamlined"} projectId={data.project.id} projectTitle={data.project.title} chapter={inspectorChapter} run={inspectorRun} events={events} agents={data.agents} />

          {/* 态势与操作控制卡 */}
          <div className="run-progress-block">
            <div className="run-progress-meta">
              <span className="run-target-chapter">
                {runActive ? `第 ${run?.currentChapter ?? inspectorChapter} 章` : `第 ${selected} 章`}
              </span>
              <strong className="run-percent">
                {Math.round(runActive ? Math.max(0, Math.min(100, inspectorRun?.progress ?? 0)) : stepperProgressPercent)}%
              </strong>
            </div>
            <div className="run-progress-bar">
              <span
                style={{
                  width: `${Math.round(runActive ? Math.max(0, Math.min(100, inspectorRun?.progress ?? 0)) : stepperProgressPercent)}%`
                }}
              />
            </div>
            <div className="inspector-actions">
              {runActive ? (
                <>
                  {run?.status === "paused" || run?.status === "needs_attention" ? (
                    <button className="button button-primary" type="button" disabled={controlBusy} onClick={() => void controlRun("resume")}>
                      <Play size={13} />继续生成
                    </button>
                  ) : (
                    <button className="button button-secondary" type="button" disabled={controlBusy} onClick={() => void controlRun("pause")}>
                      <Pause size={13} />暂停
                    </button>
                  )}
                  <button className="button button-danger" type="button" disabled={controlBusy} aria-label="停止生成" onClick={() => void controlRun("stop")}>
                    <Square size={12} />停止
                  </button>
                </>
              ) : (
                <button
                  className="button button-primary"
                  type="button"
                  onClick={openRunDialog}
                  disabled={!data.pi.authenticated || !hasNextChapter || !nextChapterWritable || planningActive || runStarting || planExtensionBusy || saving || actionBusy || feedbackBusy}
                  title={!data.pi.authenticated ? "请先在 Agent 工作室连接模型" : !nextChapterWritable ? "下一章章卡尚未通过审查" : planningActive ? "请等待章卡规划完成" : "启动下一章写作"}
                >
                  <Play size={13} />生成第 {data.project.currentChapter + 1} 章
                </button>
              )}
            </div>
          </div>

          {/* 实时步骤指示卡片 — 随时看清到哪一步 */}
          <div className={`pipeline-spotlight-card ${runActive ? "running" : detail?.status === "final" ? "final" : "idle"}`}>
            <div className="spotlight-badge-row">
              <span className="spotlight-badge">
                {run?.status === "running" ? (
                  <span className="spinner tiny" />
                ) : detail?.status === "final" ? (
                  <CheckCircle2 size={13} />
                ) : (
                  <Sparkles size={13} />
                )}
                <span>{runActive && run ? runStatusLabel(run.status) : "章节流水线状态"}</span>
              </span>
              {runActive && (
                <span className="spotlight-active-agent">
                  {currentActiveAgentName || "等待调度"}
                </span>
              )}
            </div>
            <strong className="spotlight-title">
              {runActive
                ? `第 ${run?.currentChapter} 章 · ${run?.status === "running" ? currentActiveStageName || "等待阶段更新" : runStatusLabel(run!.status)}`
                : detail?.status === "final"
                ? `第 ${selected} 章 · 已定稿`
                : detail?.draft || (detail?.words && detail.words > 0)
                ? `第 ${selected} 章 · 初稿就绪待质检`
                : detail?.synopsisStatus === "ready" || detail?.status === "ready"
                ? `第 ${selected} 章 · 章卡已就绪`
                : `第 ${selected} 章 · 章纲规划中`}
            </strong>
            <p className="spotlight-desc">
              {runActive
                ? run?.status === "running" ? `${currentActiveAgentName ? `Agent【${currentActiveAgentName}】` : "任务"}正在执行，实时记录会持续更新。` : run?.status === "paused" ? "任务已暂停，点击继续后恢复；已保存正文不受影响。" : run?.status === "needs_attention" ? "任务需要处理，请查看告警与阶段详情后继续或停止。" : "任务已进入队列，等待开始执行。"
                : detail?.status === "final"
                ? detail.score == null ? "本章已定稿，但未记录综合评分；各阶段是否执行请查看实际审计记录。" : `已记录综合评分 ${detail.score.toFixed(1)} 分；具体审查与门禁结果以执行记录为准。`
                : detail?.draft || (detail?.words && detail.words > 0)
                ? "草稿正文已完成，等待进入独立评审与出版门禁"
                : detail?.synopsisStatus === "ready" || detail?.status === "ready"
                ? "章卡合同已通过审查，可随时点击上方按钮启动生成"
                : "当前章节尚未开始写作，需先就绪章卡"}
            </p>
          </div>

          {/* 中部：四大核心创作阶段脉络 */}
          <div className="pipeline-timeline-section">
            <div className="timeline-section-header">
              <div className="timeline-header-left">
                <span className="timeline-pulse-dot" />
                <span>创作阶段流水线</span>
              </div>
              <span className="timeline-agent-state">{currentAgentIds.size > 0 ? `${currentAgentIds.size} 个 Agent 运行中` : "待命"}</span>
            </div>
            <div className="milestones-container">
              {/* 垂直主干导线 */}
              <div className="milestones-track-spine" />
              {effectiveMilestones.map((group, index) => {
                const isExpanded = expandedMilestones.has(group.id);
                return (
                  <div key={group.id} className={`milestone-card milestone-${group.status} ${isExpanded ? "expanded" : ""}`}>
                    <button type="button" className="milestone-card-header" aria-label={`${group.title}详情`} aria-expanded={isExpanded} aria-controls={`milestone-${group.id}`} onClick={() => toggleMilestone(group.id)}>
                      <span className="milestone-badge">
                        {group.status === "passed" ? (
                          <CheckCircle2 size={14} />
                        ) : group.status === "running" ? (
                          <span className="spinner tiny" />
                        ) : group.status === "failed" ? (
                          <AlertTriangle size={14} />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </span>
                      <div className="milestone-copy">
                        <strong>{group.title}</strong>
                        <small>
                          {group.status === "running" && group.activeStage
                            ? `正在进行 · ${group.activeStage.label}`
                            : group.shortStatusText}
                        </small>
                      </div>
                      <span className={`milestone-pill pill-${group.status}`}>
                        {pipelineStatusLabel(group.status)}
                      </span>
                      {group.stages.length > 0 && (
                        <ChevronDown size={13} className={`milestone-chevron ${isExpanded ? "open" : ""}`} />
                      )}
                    </button>

                    <div id={`milestone-${group.id}`} inert={!isExpanded} aria-hidden={!isExpanded} className={`milestone-collapsible ${isExpanded ? "open" : ""}`}>
                      <div className="milestone-collapsible-inner">
                        <div className="milestone-steps-sublist">
                          {group.items.map((item) => {
                            const itemLabel = item.id === "context" ? `第 ${inspectorChapter} 章资料编排` : item.label;
                            return (
                              <button
                                className={`pipeline-step pipeline-${item.status} sub-step`}
                                type="button"
                                key={item.id}
                                onClick={() => void openAgentOrStageDetail(item)}
                                title={`点击查看【${itemLabel}】详细工作内容`}
                              >
                                <span className="pipeline-index">
                                  {item.status === "passed" ? <Check size={11} strokeWidth={2.8} /> : item.status === "running" ? <span className="spinner tiny" /> : item.status === "failed" ? <AlertTriangle size={11} /> : "•"}
                                </span>
                                <span className="pipeline-copy">
                                  <strong>{itemLabel}</strong>
                                  <span>{item.detail}</span>
                                </span>
                                <span className="pipeline-agent-badge">{item.agentShortName}</span>
                                <ChevronRight size={12} className="pipeline-detail-chevron" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 下部：实时记录流 */}
          <div className="telemetry-live-panel">
            <div className="telemetry-panel-header">
              <div className="telemetry-title-badge">
                <Terminal size={13} />
                <strong>实时记录</strong>
                <span className="log-count-pill">{operationalEvents.length}</span>
              </div>
              <div className="telemetry-filter-pills">
                <button
                  type="button"
                  aria-pressed={logFilter === "all"} className={logFilter === "all" ? "active" : ""}
                  onClick={() => setLogFilter("all")}
                >
                  全部
                </button>
                <button
                  type="button"
                  aria-pressed={logFilter === "decision"} className={logFilter === "decision" ? "active" : ""}
                  onClick={() => setLogFilter("decision")}
                >
                  自愈
                </button>
                <button
                  type="button"
                  aria-pressed={logFilter === "error"} className={logFilter === "error" ? "active" : ""}
                  onClick={() => setLogFilter("error")}
                >
                  告警
                </button>
              </div>
            </div>

            <div className="ux-log-follow"><label><input type="checkbox" checked={autoScrollLogs} onChange={(event) => setAutoScrollLogs(event.target.checked)} />跟随最新记录</label><button type="button" className="button button-ghost" aria-pressed={logFilter === "score"} onClick={() => setLogFilter("score")}>评分记录</button></div>
            <div className="telemetry-log-stream" ref={logScrollRef} onScroll={(event) => { const node = event.currentTarget; if (node.scrollHeight - node.scrollTop - node.clientHeight > 50 && autoScrollLogs) setAutoScrollLogs(false); }}>
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <div key={event.id} className={`log-entry log-${event.level}`}>
                    <div className="log-entry-meta">
                      <time>{new Date(event.timestamp).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</time>
                      {event.chapter && <span className="log-chapter-tag">第 {event.chapter} 章</span>}
                      {event.stageId && <span className="log-stage-tag">{event.stageId}</span>}
                    </div>
                    <p className="log-entry-msg">{event.message}</p>
                  </div>
                ))
              ) : (
                <div className="telemetry-empty">
                  <span>{logFilter === "all" ? "暂无事件动态" : "当前筛选暂无记录"}</span>{logFilter !== "all" && <button type="button" className="button button-ghost" onClick={() => setLogFilter("all")}>查看全部记录</button>}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <Modal
        open={Boolean(stageDetailTarget || selectedAgentInfo)}
        title={stageDetail?.title ?? stageDetailTarget?.stage.label ?? selectedAgentInfo?.agentName ?? "Agent 工作详情"}
        description={stageDetailTarget ? `第 ${stageDetail?.chapter ?? stageDetailTarget.chapter} 章 · ${pipelineStatusLabel(stageDetail?.status ?? inspectorRun?.stages.find((stage) => stage.id === stageDetailTarget.stage.id)?.status ?? stageDetailTarget.stage.status)}` : undefined}
        width="large"
        onClose={() => { stageSequence.current++; setStageDetailTarget(null); setStageDetail(null); setSelectedAgentInfo(null); }}
        footer={<button className="button button-secondary" type="button" onClick={() => { stageSequence.current++; setStageDetailTarget(null); setStageDetail(null); setSelectedAgentInfo(null); }}>关闭</button>}
      >
        {stageError && <div className="ux-error" role="alert"><p>{stageError}</p><button className="button button-secondary" type="button" disabled={stageDetailBusy || !stageDetailTarget} onClick={() => { if (stageDetailTarget) void openStageDetail(stageDetailTarget.stage); }}>重新读取执行记录</button></div>}
        {stageDetailBusy ? (
          <div className="stage-detail-loading"><span className="spinner dark" /></div>
        ) : stageDetail ? (
          <StageDetailView detail={stageDetail} />
        ) : selectedAgentInfo ? (
          <AgentDossierView key={`${selectedAgentInfo.chapter}:${selectedAgentInfo.stageId}`}
            agentInfo={selectedAgentInfo}
            agentConfig={data.agents.find((a) => a.id === selectedAgentInfo.agentId)}
            chapterDetail={detail?.number === selectedAgentInfo.chapter ? detail : null}
            scoreRounds={selectedAgentInfo.chapter === selected ? scoreRounds : []}
            threshold={data.settings.scoreThreshold}
          />
        ) : (
          <EmptyState icon={<FileText size={22} />} title="暂无详细审计数据" detail="该阶段尚未开始或尚未生成审计快照。" />
        )}
      </Modal>

      <Modal open={runDialog} busy={runStarting} title="启动章节流水线" description="正文严格串行，每章创建独立写作 Agent；调用模型将产生费用。" onClose={() => setRunDialog(false)} footer={<><button type="button" disabled={runStarting} className="button button-ghost" onClick={() => setRunDialog(false)}>取消</button><button type="submit" form="run-form" className="button button-primary" disabled={runStarting || !runRangeValid || runActive || planningActive || !data.pi.authenticated || dirty}>{runStarting ? <span className="spinner" /> : <Play size={16} />}{runStarting ? "正在启动…" : "开始写作"}</button></>}>
        <p className="ux-note" role="status">{!data.pi.authenticated ? "请先在 Agent 管理中连接可用模型。" : !runRangeValid ? `请从下一章 ${data.project.currentChapter + 1} 开始，结束章必须是 ${data.project.currentChapter + 1}–${approvedPlanThrough} 之间的整数。` : `将连续生成第 ${startChapter}–${endChapter} 章，共 ${endChapter - startChapter + 1} 章。任务启动后可暂停或停止。`}</p>
        <form id="run-form" className="run-form" onSubmit={submitRun}>
          <div className="field-grid two">
            <label>
              <span>起始章节</span>
              <input
                type="number"
                min={data.project.currentChapter + 1}
                max={approvedPlanThrough}
                readOnly step={1}
                value={startChapter}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setStartChapter(value);
                  if (endChapter < value) setEndChapter(value);
                }}
              />
            </label>
            <label>
              <span>结束章节</span>
              <input
                type="number"
                min={startChapter}
                max={approvedPlanThrough}
                disabled={runStarting} required step={1}
                value={endChapter}
                onChange={(event) => setEndChapter(Number(event.target.value))}
              />
            </label>
          </div>

          <div className="run-presets-bar">
            <span className="preset-label">快捷批次：</span>
            <div className="preset-chips-list">
              {[1, 3, 5, 10].map((step) => {
                const target = startChapter + step - 1;
                const disabled = runStarting || target < startChapter || target > approvedPlanThrough;
                const active = endChapter === target;
                return (
                  <button
                    key={step}
                    type="button"
                    aria-pressed={active} className={`preset-chip ${active ? "active" : ""}`}
                    disabled={disabled}
                    onClick={() => setEndChapter(target)}
                  >
                    +{step} 章{step === 1 ? " (单章)" : step === 10 ? " (十章)" : ""}
                  </button>
                );
              })}
              {approvedPlanThrough > startChapter && (
                <button
                  type="button"
                  className={`preset-chip ${endChapter === approvedPlanThrough ? "active" : ""}`}
                  disabled={runStarting} onClick={() => setEndChapter(approvedPlanThrough)}
                >
                  至已审章卡 (第 {approvedPlanThrough} 章)
                </button>
              )}
            </div>
          </div>

          <div className="run-summary-row">
            <span><StatusDot status="running" />真实 Pi 调用</span>
            <span>{Math.max(1, endChapter - startChapter + 1)} 章正文串行写作</span>
            <span>每 {data.settings.checkpointEvery} 章批检</span>
          </div>

          {/* 阶段化流水线 Agent 路线图 */}
          <div className="run-pipeline-visual">
            <div className="pipeline-visual-phase">
              <span className="phase-title">1. 临写与创作</span>
              <div className="phase-agents">
                {["chapter-planner", "writer"].map((id) => {
                  const agent = data.agents.find((item) => item.id === id);
                  return agent ? (
                    <span key={id} className="agent-tag" title={agent.name}>
                      <i style={{ background: agent.accent }} />
                      <strong>{agent.shortName}</strong>
                      <small>{agent.model.split("/").pop()}</small>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div className="pipeline-flow-arrow">→</div>
            <div className="pipeline-visual-phase">
              <span className="phase-title">2. 原稿直评</span>
              <div className="phase-agents scorers">
                {(data.settings.reviewWorkflow === "legacy" ? ["plot-scorer", "continuity-scorer", "character-scorer", "style-scorer", "reader-scorer"] : ["continuity-scorer", "literary-scorer", "reader-scorer"]).map((id) => {
                  const agent = data.agents.find((item) => item.id === id);
                  return agent ? (
                    <span key={id} className="agent-tag compact" title={agent.name}>
                      <i style={{ background: agent.accent }} />
                      <strong>{agent.shortName}</strong>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div className="pipeline-flow-arrow">→</div>
            <div className="pipeline-visual-phase">
              <span className="phase-title">3. 按需返修</span>
              <div className="phase-agents">
                {["writer", "optimizer"].map((id) => {
                  const agent = data.agents.find((item) => item.id === id);
                  return agent ? (
                    <span key={id} className="agent-tag" title={id === "optimizer" ? "仅有正文证据的 line 问题才调用" : "scene / chapter 问题才回投 Writer"}>
                      <i style={{ background: agent.accent }} />
                      <strong>{agent.shortName}</strong>
                      <small>{id === "optimizer" ? "有证据时" : "结构问题"}</small>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div className="pipeline-flow-arrow">→</div>
            <div className="pipeline-visual-phase">
              <span className="phase-title">4. 终审与事实结算</span>
              <div className="phase-agents">
                {["gate", "fact-observer", "ledger", "state-validator"].map((id) => {
                  const agent = data.agents.find((item) => item.id === id);
                  return agent ? (
                    <span key={id} className="agent-tag" title={agent.name}>
                      <i style={{ background: agent.accent }} />
                      <strong>{agent.shortName}</strong>
                      <small>{agent.model.split("/").pop()}</small>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={feedbackOpen} busy={feedbackBusy} title={`第 ${selected} 章作者修改意见`} description="按修改要求定向调整正文，随后自动复审" width="large" onClose={() => !feedbackBusy && setFeedbackOpen(false)} footer={<button type="button" className="button button-ghost" disabled={feedbackBusy} onClick={() => setFeedbackOpen(false)}>关闭，保留输入</button>}>
        <p className="ux-note" role="status">{dirty ? "正文或章卡尚未保存，请关闭此窗口先保存；修改意见会保留。" : feedbackText.trim().length < 5 ? `修改要求至少 5 字，还需 ${5 - feedbackText.trim().length} 字。` : "提交后会创建定向改稿与复审任务；同一时间请勿重复提交。"}</p>
        {feedbackLoadBusy && <p role="status">正在读取修改任务与历史…</p>}
        {feedbackLoadError && <div className="ux-error" role="alert"><p>{feedbackLoadError}</p><button type="button" className="button button-secondary" disabled={feedbackLoadBusy} onClick={() => void openFeedback()}>重新读取修改任务</button></div>}
        <form className="chapter-feedback-form" onSubmit={submitFeedback}><label><span>本次修改要求</span><textarea value={feedbackText} onChange={(event) => setFeedbackText(event.target.value)} placeholder="例如：删除开头两段设定说明；加强唐三的试探，但不能让林曜泄露卫星数量；结尾自然承接下一章。" disabled={feedbackBusy || Boolean(activeUserRevision)} /></label><button className="button button-primary" type="submit" disabled={editorLocked || feedbackLoadBusy || Boolean(feedbackLoadError) || Boolean(activeUserRevision) || feedbackText.trim().length < 5 || dirty}>{feedbackBusy ? <span className="spinner" /> : <Send size={15} />}提交给写作 Agent</button></form>
        {activeUserRevision && <section className="active-feedback-task"><header><span className="spinner dark" /><div><strong>{userRevisionStatus(activeUserRevision.status)}</strong><small>{activeUserRevision.detail}</small></div><em>{activeUserRevision.progress}%</em></header><div><span style={{ width: `${activeUserRevision.progress}%` }} /></div></section>}
        {userRevisions.length > 0 && <section className="feedback-history"><h3>历史修改意见</h3>{userRevisions.map((item) => <article key={item.id}><header><span>{userRevisionStatus(item.status)}</span><time>{new Date(item.createdAt).toLocaleString("zh-CN")}</time></header><p>{item.instruction}</p>{item.error && <small>{item.error}</small>}</article>)}</section>}
      </Modal>

      <Modal open={chapterAction === "delete"} busy={actionBusy} title={`删除第 ${selected} 章正文`} description="正文与账本将归档至历史快照" width="small" onClose={() => !actionBusy && setChapterAction(null)} footer={<><button type="button" className="button button-ghost" onClick={() => setChapterAction(null)} disabled={actionBusy}>取消</button><button type="button" className="button button-danger" onClick={deleteChapter} disabled={actionBusy}>{actionBusy ? <span className="spinner" /> : <Trash2 size={15} />}确认删除</button></>}>
        <div className="chapter-action-confirm"><Trash2 size={21} /><p>本章将回到待写状态，确认后会将旧正文、评分与账本保存为快照；删除前尚未保存的输入不在快照中。</p></div>
      </Modal>

      <Modal open={chapterAction === "rewrite"} busy={actionBusy} title={`重写第 ${selected} 章`} description="归档旧稿并重新创作" width="small" onClose={() => !actionBusy && setChapterAction(null)} footer={<><button type="button" className="button button-ghost" onClick={() => setChapterAction(null)} disabled={actionBusy}>取消</button><button type="button" className="button button-primary" onClick={rewriteChapter} disabled={actionBusy}>{actionBusy ? <span className="spinner" /> : <RotateCcw size={15} />}开始重写</button></>}>
        <div className="chapter-action-confirm rewrite"><RotateCcw size={21} /><p>将基于前章终稿与本章章卡重新写作，确认后会将已保存旧稿归档，再启动新的模型写作任务。</p></div>
      </Modal>

      <Modal open={historyOpen} busy={actionBusy} title={`第 ${selected} 章版本历史`} description="正文保存与各阶段快照" width="large" onClose={() => !actionBusy && setHistoryOpen(false)}>
        {historyBusy ? <div className="history-loading"><span className="spinner dark" /></div> : historyError ? <div className="ux-error" role="alert"><p>{historyError}</p><button className="button button-secondary" type="button" onClick={() => void openHistory()}>重新读取历史版本</button></div> : revisions.length ? <div className="chapter-revision-list">{revisions.map((revision) => <article key={revision.id}>
          <header><span><strong>版本 {revision.version}</strong><small>{revisionSourceLabel(revision.source)} · {new Date(revision.createdAt).toLocaleString("zh-CN")}</small></span><span className={`status-pill status-pill-${revision.status}`}><i />{revision.status === "final" ? "已定稿" : "草稿"}</span></header>
          <p>{revision.content.slice(0, 260) || "空正文"}{revision.content.length > 260 ? "…" : ""}</p><details><summary>查看完整历史正文</summary><pre>{revision.content || "空正文"}</pre></details>
          <footer><span>{formatNumber(revision.words)} 字</span><span>{revision.ledgerCount} 条账本</span>{revision.score !== null && <span>评分 {revision.score.toFixed(1)}</span>}<button type="button" className="button button-secondary" onClick={() => restoreRevision(revision)} disabled={editorLocked || loadingChapter}><History size={14} />恢复此版本</button></footer>
        </article>)}</div> : <EmptyState icon={<History size={22} />} title="尚无历史版本" detail="下一次保存、重写或删除时会自动创建" />}
      </Modal>

      <Modal open={rebuildHistoryOpen} title="章卡重建记录" width="large" onClose={() => setRebuildHistoryOpen(false)} footer={<><button className="button button-secondary" type="button" onClick={() => setRebuildHistoryOpen(false)}>关闭</button>{data.planRebuild?.status === "failed" && onOpenBible && <button className="button button-primary" type="button" onClick={() => { setRebuildHistoryOpen(false); onOpenBible(); }}>检查设定并修订</button>}</>}>
        {data.planRebuild && <div className="ux-rebuild-history"><p role="status">已完成 {data.planRebuild.plansCompleted} / {data.planRebuild.totalPlans} 章卡，当前第 {data.planRebuild.currentBatch} / {data.planRebuild.totalBatches} 批。</p>{data.planRebuild.error && <p className="ux-error" role="alert">{data.planRebuild.error}</p>}{data.planRebuild.status === "failed" && <p className="ux-note">这是旧版章卡重建任务，当前接口没有安全的原任务续作入口，不能用重新创建任务冒充“继续”。请先检查错误和故事圣经，再通过“整书讨论与修订”确认后续章纲调整；不会在这里自动重写正文。</p>}{data.planRebuild.reviewHistory.length ? data.planRebuild.reviewHistory.map((item, index) => <article key={index}><strong>第 {item.chapter} 章 · 第 {item.attempt} 轮 · {planActionLabel(item.action)}</strong><small>{new Date(item.timestamp).toLocaleString("zh-CN")}</small><p>{item.message}</p></article>) : <p>尚无审查记录，不能据此视为通过。</p>}</div>}
      </Modal>
      {/* 全书章节目录全景矩阵 Modal */}
      <Modal
        open={matrixOpen}
        title="章节目录"
        description={`全书共 ${formatNumber(totalChapters)} 章 · 已定稿 ${data.project.completedChapters} 章`}
        width="large"
        onClose={() => setMatrixOpen(false)}
        footer={<button className="button button-secondary" type="button" onClick={() => setMatrixOpen(false)}>关闭</button>}
      >
        <div className="chapter-matrix-modal">
          <div className="matrix-toolbar">
            <div className="matrix-search-box">
              <Search size={14} />
              <input
                type="search" aria-label="搜索创作台章节目录"
                placeholder="按章节号或标题搜索..."
                value={matrixSearch}
                onChange={(e) => setMatrixSearch(e.target.value)}
                autoFocus
              />
              {matrixSearch && (
                <button type="button" aria-label="清空目录搜索" className="matrix-clear-btn" onClick={() => setMatrixSearch("")}>
                  <X size={13} />
                </button>
              )}
            </div>
            <div className="matrix-filters">
              <button
                type="button"
                aria-pressed={matrixFilter === "all"} className={`matrix-filter-pill ${matrixFilter === "all" ? "active" : ""}`}
                onClick={() => setMatrixFilter("all")}
              >
                <span>全部</span>
                <small>{data.chapters.length}</small>
              </button>
              <button
                type="button"
                aria-pressed={matrixFilter === "final"} className={`matrix-filter-pill ${matrixFilter === "final" ? "active" : ""}`}
                onClick={() => setMatrixFilter("final")}
              >
                <span>已定稿</span>
                <small>{data.chapters.filter((c) => c.status === "final").length}</small>
              </button>
              <button
                type="button"
                aria-pressed={matrixFilter === "ready"} className={`matrix-filter-pill ${matrixFilter === "ready" ? "active" : ""}`}
                onClick={() => setMatrixFilter("ready")}
              >
                <span>可开写</span>
                <small>{data.chapters.filter((c) => c.status === "ready").length}</small>
              </button>
              <button
                type="button"
                aria-pressed={matrixFilter === "draft"} className={`matrix-filter-pill ${matrixFilter === "draft" ? "active" : ""}`}
                onClick={() => setMatrixFilter("draft")}
              >
                <span>草稿 / 待写</span>
                <small>{data.chapters.filter((c) => c.status === "draft" || c.status === "planned").length}</small>
              </button>
            </div>
          </div>

          <div className="matrix-grid-scroll">
            {matrixChapters.length > 0 ? (
              <div className="matrix-grid">
                {visibleMatrixChapters.map((c) => (
                  <button
                    key={c.number}
                    type="button"
                    aria-current={c.number === selected ? "true" : undefined} className={`matrix-cell ${c.number === selected ? "selected" : ""} status-${c.status}`}
                    onClick={async () => { if (await setSelected(c.number)) setMatrixOpen(false); }}
                    title={`第 ${c.number} 章: ${c.title}`}
                  >
                    <span className="matrix-cell-num">{c.number}</span>
                    <strong className="matrix-cell-title">{c.title}</strong>
                    {c.score !== null ? (
                      <span className="matrix-cell-score">{c.score.toFixed(1)}</span>
                    ) : c.status === "final" ? (
                      <span className="matrix-cell-badge badge-final">定稿</span>
                    ) : c.status === "ready" ? (
                      <span className="matrix-cell-badge badge-ready">可写</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : (
              <div className="matrix-empty">
                <span>未找到符合当前筛选的章节</span><button className="button button-secondary" type="button" onClick={() => { setMatrixSearch(""); setMatrixFilter("all"); }}>清空搜索与筛选</button>
              </div>
            )}
            <div className="ux-matrix-pages"><button className="button button-secondary" type="button" disabled={currentMatrixPage <= 1} onClick={() => setMatrixPage(currentMatrixPage - 1)}>上一页章节</button><span role="status">共 {matrixChapters.length} 章 · 第 {currentMatrixPage} / {matrixPages} 页 · 每页 100 章</span><button className="button button-secondary" type="button" disabled={currentMatrixPage >= matrixPages} onClick={() => setMatrixPage(currentMatrixPage + 1)}>下一页章节</button></div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function StageDetailView({ detail }: { detail: RunStageDetail }) {
  return <div className="stage-detail-view">
    {detail.summary && <p className="stage-detail-summary">{detail.summary}</p>}
    <CreativeWorkflowReceipt detail={detail} />
    <ChapterTitleReceipt detail={detail} />
    <section className="stage-detail-metrics">
      <span><small>输入估算</small><strong>{formatNumber(detail.inputTokens)} tokens</strong></span>
      <span><small>输入字符</small><strong>{formatNumber(detail.inputCharacters)}</strong></span>
      <span><small>输出字符</small><strong>{detail.outputCharacters ? formatNumber(detail.outputCharacters) : "--"}</strong></span>
      <span><small>资料分段</small><strong>{detail.sections.length}</strong></span>
    </section>
    
    <ModelCallReceipt detail={detail} />
    {detail.agents.length > 0 && <section className="stage-detail-agents"><h3>Agent 与模型</h3>{detail.agents.map((agent) => <article key={agent.id}><span><strong>{agent.name}</strong><small>{agent.id}</small></span><code>{agent.model}</code><span><small>思考等级</small><strong>{thinkingLevelLabel(agent.thinkingLevel)}</strong></span><span><small>输入估算</small><strong>{formatNumber(agent.estimatedInputTokens)} tokens</strong></span></article>)}</section>}
    {detail.notes.length > 0 && <section className="stage-detail-notes"><h3>执行说明</h3>{detail.notes.map((note, index) => <p key={`${note}-${index}`}>- {note}</p>)}</section>}
    {detail.findings.length > 0 && <section className="stage-detail-notes"><h3>批检发现与复核项</h3>{detail.findings.map((finding, index) => <p key={`${finding}-${index}`}>- {finding}</p>)}</section>}
    
    
    <section className="stage-detail-sections"><header><h3>实际输入内容</h3><span>{detail.sections.length} 段</span></header>{detail.sections.length ? detail.sections.map((section, index) => <details key={section.id} open={index === 0 && !detail.chapterMemo && !detail.factObservation}><summary><span><strong>{section.label}</strong><small>{section.source}</small></span><span>{formatNumber(section.estimatedTokens)} tokens · {formatNumber(section.characters)} 字符</span></summary><pre>{section.content}</pre></details>) : <EmptyState icon={<FileText size={20} />} title="本阶段没有模型输入" detail={detail.notes[0]} />}</section>
    <footer className="stage-detail-footer">最后更新 {new Date(detail.updatedAt).toLocaleString("zh-CN")}</footer>
  </div>;
}

function ScoreRoundHistory({ rounds, publishThreshold, expanded, onToggle }: { rounds: ScoreRound[]; publishThreshold: number; expanded: number | null; onToggle: (round: number) => void }) {
  return <section className="score-round-history"><header><span>评分与返修依据</span><small>{rounds.length} 轮</small></header>{rounds.map((round, index) => {
    const decision = explainScoreRound(round, publishThreshold);
    const isExpanded = expanded === round.round;
    const previous = rounds[index - 1];
    const delta = round.aggregate !== null && previous?.aggregate !== null && previous?.aggregate !== undefined ? round.aggregate - previous.aggregate : null;
    return <article key={round.round} className={isExpanded ? "expanded" : ""}><button className="round-summary-button" type="button" aria-expanded={isExpanded} onClick={() => onToggle(round.round)}><div><strong>第 {round.round} 轮</strong><small>{round.status === "partial" ? "评分未完成" : new Date(round.updatedAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</small></div><div className="round-score-chips">{round.scores.map((score) => <span key={score.agentId}>{score.dimension} {score.score.toFixed(1)}</span>)}</div><span className={`round-route route-${decision.route}`}>{decision.label}</span><em>{round.aggregate === null ? "--" : round.aggregate.toFixed(1)}{delta !== null && <small className={delta >= 0 ? "delta-up" : "delta-down"}>{delta >= 0 ? "+" : ""}{delta.toFixed(1)}</small>}</em><ChevronRight size={15} className={isExpanded ? "round-chevron open" : "round-chevron"} /></button>{isExpanded && <div className="round-explanation"><section className="decision-explanation"><header><strong>为什么这样处理</strong><span>观测参考线 {decision.threshold.toFixed(1)}</span></header>{decision.reasons.length ? decision.reasons.map((reason, reasonIndex) => <div key={`${reason.type}-${reasonIndex}`}><span className={`scope-badge scope-${reason.scope}`}>{scopeText(reason.scope)}</span><p>{reason.message}</p>{reason.evidence && <small>证据：{reason.evidence}</small>}{reason.rule && <small>依据：{reason.rule}</small>}</div>) : <p>本轮没有需要修改的问题，继续进入硬事实门禁。</p>}</section><div className="agent-score-explanations">{round.scores.map((score) => <section key={score.agentId}><header><div><strong>{score.dimension}评分 · {score.score.toFixed(1)}</strong><small>{score.verdict}</small><small className="review-provenance">{reviewReceiptLabel(score)}</small></div><span className={`scope-badge scope-${score.fixScope ?? "none"}`}>{score.hardPass === false ? "硬性未通过" : scopeText(score.fixScope ?? "none")}</span></header>{scoreIssueDetails(score).length ? scoreIssueDetails(score).map((issue, issueIndex) => <article key={issueIndex}><p>{issue.description}</p>{issue.evidence && <small>正文证据：{issue.evidence}</small>}{issue.rule && <small>评分规则：{issue.rule}</small>}</article>) : <div className="score-no-issues"><Check size={13} />本维度没有需要修改的问题</div>}</section>)}</div></div>}</article>;
  })}</section>;
}

function explainScoreRound(round: ScoreRound, publishThreshold: number): { route: "pass" | "writer" | "polish" | "incomplete"; label: string; threshold: number; reasons: ScoreDecisionReason[] } {
  const threshold = round.revisionThreshold ?? publishThreshold;
  const stored = round.reasons ?? [];
  const hardScores = round.scores.filter((score) => !["style-scorer", "reader-scorer", "narrative-editor"].includes(score.agentId));
  const scopes = hardScores.map((score) => score.fixScope ?? (score.structural ? "chapter" : score.issues.length ? "line" : "none"));
  const hardFailures = hardScores.filter((score) => score.hardPass === false);
  const editorialFailures = round.scores.filter((score) => ["style-scorer", "reader-scorer", "narrative-editor"].includes(score.agentId)
    && score.score < threshold && scoreIssueDetails(score).length > 0);
  const needsWriter = round.status === "partial" || hardFailures.length > 0 || editorialFailures.length > 0 || scopes.some((scope) => scope === "chapter");
  const route = round.status === "partial" ? "incomplete" : round.decision ?? "incomplete";
  const reasons = route === "incomplete" && round.status !== "partial" && !round.decision ? [{ type: "incomplete" as const, message: "未记录编排裁决；不能根据分数推断已经通过或必须返修。", scope: "none" as const, agents: [], evidence: "", rule: "以实际编排记录为准" }] : route === "pass" ? stored : stored.length ? stored : deriveRoundReasons(round, threshold, route);
  return { route, label: route === "pass" ? "编排通过" : route === "writer" ? "Writer定向返修" : route === "polish" ? "选择性精修" : round.status === "partial" ? "等待补评" : "等待裁决", threshold, reasons };
}

function deriveRoundReasons(round: ScoreRound, threshold: number, route: "pass" | "writer" | "polish" | "incomplete"): ScoreDecisionReason[] {
  const reasons: ScoreDecisionReason[] = [];
  if (round.status === "partial") reasons.push({ type: "incomplete", message: "至少一个评审 Agent 没有成功返回，本轮保留已有结果并只重试缺失项。", scope: "none", agents: [], evidence: "", rule: "全部启用评审维度必须齐全" });
  if (round.aggregate !== null && round.aggregate < threshold) reasons.push({ type: "threshold", message: `综合观测分 ${round.aggregate.toFixed(1)} 低于参考线 ${threshold.toFixed(1)}；该数字只供编排 Agent 理解分歧，不直接触发改稿。`, scope: "none", agents: [], evidence: "", rule: "返修动作由编排 Agent 综合裁决" });
  for (const score of round.scores) {
    const scope = score.fixScope ?? (score.structural ? "chapter" : score.issues.length ? "line" : "none");
    if (score.hardPass === false) reasons.push({ type: "hard_failure", message: `${score.dimension}评分判定存在硬设定、章卡核心或人物权限冲突。`, scope: scope === "none" ? "scene" : scope, agents: [score.dimension], evidence: scoreIssueDetails(score)[0]?.evidence ?? "", rule: scoreIssueDetails(score)[0]?.rule ?? "hardPass=false" });
    for (const issue of scoreIssueDetails(score).filter((item) => item.scope === "scene" || item.scope === "chapter" || route === "polish")) reasons.push({ type: "issue", message: `${score.dimension}：${issue.description}`, scope: issue.scope, agents: [score.dimension], evidence: issue.evidence, rule: issue.rule });
  }
  return reasons.slice(0, 12);
}

function scoreIssueDetails(score: ScoreResult): Array<{ description: string; evidence: string; rule: string; scope: Exclude<FixScope, "none"> }> {
  if (Array.isArray(score.issueDetails) && score.issueDetails.length) return score.issueDetails;
  const fallback = score.fixScope && score.fixScope !== "none" ? score.fixScope : score.structural ? "chapter" : "line";
  return (score.issues ?? []).map((description) => ({ description, evidence: "", rule: "", scope: fallback }));
}

function scopeText(scope: FixScope): string { return ({ none: "无需修复", line: "逐句", scene: "场景", chapter: "整章" })[scope]; }

function contractLines(value: string, limit: number): string[] {
  const lines = value.split(/\n+/).map((item) => item.trim()).filter(Boolean);
  if (lines.length > limit) throw new Error(`当前字段最多 ${limit} 项，请先合并或删减。你的输入不会被自动截断。`);
  return lines;
}
function approvedPlanThroughValue(chapters: BootstrapPayload["chapters"]): number {
  let through = 0;
  for (const chapter of chapters) { if (chapter.number !== through + 1 || chapter.synopsisStatus !== "ready") break; through = chapter.number; }
  return through;
}

function planActionLabel(action: string): string {
  return ({ review_failed: "审查未通过", review_passed: "审查通过", arbiter_ruling: "主 Agent 裁决", direction_failed: "方向偏离", direction_passed: "方向审查通过" } as Record<string, string>)[action] ?? action;
}

function userRevisionStatus(status: ChapterUserRevision["status"]): string {
  return ({ queued: "等待处理", running: "写作 Agent 修改中", reviewing: "评分与门禁中", completed: "已完成", failed: "处理失败" })[status];
}

function revisionSourceLabel(source: ChapterRevision["source"]): string {
  return ({ manual: "手动保存", agent: "Agent 返修", replan: "重做章卡前", rewrite: "重写前", delete: "删除前", restore: "恢复前", finalize: "定稿前", checkpoint: "批检自动修复" })[source];
}

function runStatusLabel(status: NovelRun["status"]): string {
  return ({ queued: "排队中", running: "运行中", paused: "已暂停", completed: "已完成", needs_attention: "需处理", failed: "失败", stopped: "已停止" })[status];
}

function chapterStatusLabel(status?: ChapterDetail["status"]): string {
  return status ? ({ final: "已定稿", draft: "草稿", ready: "待开写", planned: "已规划", writing: "写作中", reviewing: "评审中" })[status] : "尚未载入";
}

function pipelineStatusLabel(status: PipelineStage["status"]): string {
  return ({ waiting: "等待", running: "运行中", passed: "已通过", failed: "未通过", skipped: "已跳过" })[status];
}

function thinkingLevelLabel(level: string): string {
  return ({ off: "关闭", minimal: "极低", low: "低", medium: "中", high: "高", xhigh: "很高", max: "最高" } as Record<string, string>)[level] ?? level;
}

function agentGlyph(kind?: string) {
  const Icon = kind === "writer" || kind === "style_analyst" ? FileText
    : kind === "scorer" ? Gauge
    : kind === "framework" ? BrainCircuit
    : kind === "optimizer" || kind === "polisher" ? Sparkles
    : kind === "gate" || kind === "planner_reviewer" ? Check
    : kind === "ledger" ? ListChecks
    : kind === "checkpoint" ? SlidersHorizontal
    : Sparkles;
  return <Icon size={18} strokeWidth={1.8} />;
}

function runElapsed(createdAt: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder].map((value) => String(value).padStart(2, "0")).join(":");
}

function AgentDossierView({
  agentInfo,
  agentConfig,
  chapterDetail,
  scoreRounds,
  threshold
}: {
  agentInfo: { agentId: string; agentName: string; stageId: string; stageLabel: string; status: string; chapter: number };
  agentConfig?: AgentConfig;
  chapterDetail: ChapterDetail | null;
  scoreRounds: ScoreRound[];
  threshold: number;
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const latestRound = scoreRounds.at(-1);

  return (
    <div className="agent-dossier-modal">
      {/* 顶部 Agent 核心名牌卡 */}
      <section className="agent-dossier-header-card">
        <div className="dossier-avatar-wrap">
          <span className="dossier-glyph">
            {agentGlyph((agentConfig?.kind as AgentConfig["kind"]) ?? "writer")}
          </span>
          <div>
            <div className="dossier-title-row">
              <strong>{agentConfig?.name || agentInfo.agentName}</strong>
              <span className={`status-pill status-pill-${agentInfo.status === "passed" ? "final" : agentInfo.status === "running" ? "writing" : "ready"}`}>
                <i />{pipelineStatusLabel(agentInfo.status as PipelineStage["status"])}
              </span>
            </div>
            <small>{agentConfig?.id || agentInfo.agentId} · {agentConfig?.kind === "writer" ? "正文写作实例" : agentConfig?.kind === "scorer" ? "独立质检实例" : "流水线核心 Agent"}</small>
          </div>
        </div>

        <div className="dossier-specs-grid">
          <div><small>模型</small><code>{agentConfig?.model ? agentConfig.model.split("/").pop() : "系统默认"}</code></div>
          <div><small>推理等级</small><strong>{thinkingLevelLabel(agentConfig?.thinkingLevel ?? "medium")}</strong></div>
          <div><small>设定提示词</small><strong>{agentConfig?.promptTokens ? `${formatNumber(agentConfig.promptTokens)} tokens` : "--"}</strong></div>
          <div><small>Prompt 版本</small><strong>v{agentConfig?.promptVersion ?? 1}</strong></div>
        </div>
      </section>

      {/* 核心职责 */}
      {agentConfig?.responsibility && (
        <section className="dossier-section">
          <h3>核心工作边界与职责</h3>
          <p className="dossier-responsibility">{agentConfig.responsibility}</p>
        </section>
      )}

      {/* 针对当前章节的工作产出与执行审计 */}
      <section className="dossier-section">
        <h3>第 {agentInfo.chapter} 章工作产出与执行记录</h3>
        <div className="dossier-output-box">
          {agentInfo.agentId === "writer" || agentInfo.stageId === "write" ? (
            chapterDetail && (chapterDetail.draft || chapterDetail.finalText) ? (
              <div className="dossier-chapter-preview">
                <div className="dossier-preview-meta">
                  <span>正文字数：<strong>{formatNumber(chapterDetail.words)} 字</strong></span>
                  <span>版本：<strong>v{chapterDetail.version}</strong></span>
                  <span>状态：<strong>{chapterDetail.status === "final" ? "已定稿通过" : "草稿待审"}</strong></span>
                </div>
                <div className="dossier-text-sample">
                  <pre>{(chapterDetail.status === "final" ? chapterDetail.finalText : chapterDetail.draft || chapterDetail.finalText).slice(0, 1000)}{(chapterDetail.status === "final" ? chapterDetail.finalText : chapterDetail.draft || chapterDetail.finalText).length > 1000 ? "…（仅展示前 1000 字符）" : ""}</pre>
                </div>
              </div>
            ) : (
              <p className="dossier-empty-note">本章正文尚未开始写作，启动流水线后将由 Writer 独立创作。</p>
            )
          ) : agentInfo.stageId === "score" || agentConfig?.kind === "scorer" ? (
            latestRound ? (
              <div className="dossier-scores-preview">
                <div className="dossier-score-aggregate">
                  <span>第 {latestRound.round} 轮综合打分：<strong>{latestRound.aggregate?.toFixed(1) ?? "--"} 分</strong></span>
                  <small>当前参考线 {threshold.toFixed(1)}，历史判定以审计记录为准</small>
                </div>
                <div className="dossier-scores-list">
                  {latestRound.scores.map((sc) => (
                    <div key={sc.agentId} className="dossier-score-item">
                      <span>{sc.dimension}</span>
                      <strong className={sc.score >= threshold ? "high" : "low"}>{sc.score.toFixed(1)}</strong>
                      <small>{sc.verdict || "未记录评语"}</small>
                    </div>
                  ))}
                </div>
              </div>
            ) : chapterDetail?.score !== null && chapterDetail?.score !== undefined ? (
              <div className="dossier-score-aggregate">
                <span>终稿综合得分：<strong>{chapterDetail.score.toFixed(1)} 分</strong></span>
                <small>当前未载入各维度审查结果</small>
              </div>
            ) : (
              <p className="dossier-empty-note">本章尚未生成评分记录，初稿生成后将由独立评审组打分。</p>
            )
          ) : agentInfo.stageId === "gate" || agentInfo.agentId === "gate" ? (
            <div className="dossier-gate-preview">
              <div className="dossier-gate-row">
                <span>门禁判定结果：</span>
                <strong className={chapterDetail?.status === "final" ? "pass" : "pending"}>
                  {agentInfo.status === "passed" ? "已记录阶段通过，详情以门禁快照为准" : "暂无本章门禁通过记录"}
                </strong>
              </div>
              <small>终审门禁依据：事实连续性、核心事件闭环、无设定穿帮与不可逆冲突。</small>
            </div>
          ) : agentInfo.stageId === "ledger" || agentInfo.agentId === "ledger" ? (
            <div className="dossier-ledger-preview">
              <div className="dossier-ledger-row">
                <span>本章动态账本状态：</span>
                <strong>{agentInfo.status === "passed" ? "已记录账本阶段完成" : "暂无本章账本提取完成记录"}</strong>
              </div>
              <small>已定稿章节将自动提取人物状态、关系网变迁与伏笔推进事实进入故事圣经。</small>
            </div>
          ) : (
            <div className="dossier-generic-preview">
              <p>没有载入本阶段执行快照。上方职责说明用于了解 Agent，不代表本章实际输入、输出或通过结果。</p>
              {chapterDetail?.objective && (
                <div className="dossier-contract-snippet">
                  <span>本章目标：{chapterDetail.objective}</span>
                  {chapterDetail.relations && <span>人物关系：{chapterDetail.relations}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 展开查看提示词与任务指令 */}
      {agentConfig && (agentConfig.systemPrompt || agentConfig.instructionPrompt) && (
        <section className="dossier-prompt-section">
          <button
            type="button"
            className="dossier-prompt-toggle"
            aria-expanded={showPrompt}
            onClick={() => setShowPrompt(!showPrompt)}
          >
            <span>{showPrompt ? "收起" : "查看"}该 Agent 系统提示词与指令</span>
            <ChevronDown size={14} className={`chevron-icon ${showPrompt ? "open" : ""}`} />
          </button>
          {showPrompt && (
            <div className="dossier-prompt-body">
              {agentConfig.systemPrompt && (
                <div className="prompt-block">
                  <small>系统设定 (System Prompt)</small>
                  <pre>{agentConfig.systemPrompt}</pre>
                </div>
              )}
              {agentConfig.instructionPrompt && (
                <div className="prompt-block">
                  <small>任务指令 (Instruction Prompt)</small>
                  <pre>{agentConfig.instructionPrompt}</pre>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
